<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="!!backButtonRoute"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
    ref="gc-base-layout"
    class="stock-adjustment"
  >
    <slot></slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="getScreenJson.title"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode === 'create'
            ? 'Create Stock Adjustment'
            : 'Stock Adjustment # ' + baseView.id
        "
        :secondaryLabel="undefined"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-screen">
      <gc-header-form
        :headerOptions="getHeaderScreenJson"
        :data="loaded ? model : {}"
        :saveTransaction="saveTransaction"
        :backButtonRoute="backButtonRoute"
        ref="gc-header-form-component"
        :getHeaderToolbar="undefined"
        :setLoading="showLoading"
        v-show="!gridExpand"
        v-if="loaded"
      ></gc-header-form>
      <div>
        <div class="gc-data-grid-so-v5">
          <dx-data-grid
            :data-source="dataGridLocalItemDataSource"
            :columns="getProductEntryColumns"
            :height="getGridHeight"
            :column-chooser="{ enabled: false }"
            ref="dx-data-grid-comp"
            :key-expr="'ID'"
            :repaint-changes-only="true"
            :show-borders="true"
            :two-way-binding-enabled="false"
            :focusedRowEnabled="true"
            @toolbar-preparing="onToolbarPreparing($event)"
            @editor-preparing="onEditorPreparing"
            @cell-prepared="onCellPrepared"
            @row-prepared="onRowPrepared"
            :on-row-removed="onRowRemoved"
            :on-editing-start="onEditingStart"
            @saved="onSaved"
            v-if="loaded"
          >
            <!-- @row-updated="onRowUpdated" -->
            <DxEditing
              :allow-updating="baseView.mode !== 'review'"
              :allow-deleting="baseView.mode !== 'review'"
              mode="cell"
              :new-row-position="'last'"
              :select-text-on-edit-start="true"
              :changes="changes"
            />

            <DxKeyboardNavigation
              :edit-on-key-press="false"
              :enter-key-action="'startEdit'"
              :enter-key-direction="'none'"
              :enabled="true"
            />

            <DxPaging :page-size="200" :enabled="false" />

            <DxPager :visible="false" />

            <template #select-box-item-template="{ data }">
              <div class="custom-auto-complete">
                <span class="auto-complete-primary">{{
                  data["Description"]
                }}</span>
                <span class="auto-complete-secondary">{{ data["Code"] }}</span>
              </div>
            </template>

            <template #Record="{ data }">
              <GcRecord
                :data="data"
                :primary="[{ dataField: 'Description1' }]"
                :secondary="[{ dataField: 'Code' }]"
                :showImage="true"
              />
            </template>

            <template #branch-record="{ data }">
              <GcRecord
                :data="data"
                :primary="[{ dataField: 'BranchDescription' }]"
                :secondary="[{ dataField: 'BranchCode' }]"
              />
            </template>
            <template #sales-um-edit-cell-template="{ data: cellInfo }">
              <div
                v-bind:class="
                  cellInfo.data.SLUMLength < 2 ? 'cell-disabled' : 'sales-um'
                "
              >
                <!-- @initialized="onSalesUMInitialized" -->
                <DxSelectBox
                  :value="cellInfo.value"
                  :items="cellInfo.data.UMDataSource"
                  @value-changed="onSalesUMRowClick($event, cellInfo)"
                  @focus-in="onSalesUMFocusIn($event, cellInfo)"
                  valueExpr="UnitOfMeasure"
                  displayExpr="UnitOfMeasure"
                  :drop-down-options="{ width: 250 }"
                  :tabIndex="0"
                >
                </DxSelectBox>
              </div>
            </template>
            <DxMasterDetail
              :enabled="true"
              template="masterDetailTemplate"
              :key="detailKey"
            />
            <template #masterDetailTemplate="{ data: lineItem }">
              <dx-data-grid
                :data-source="lineItem.data.ChildDataSource"
                :columns="getChildGridColumns(lineItem)"
                :columnChooser="{
                  enabled: false,
                }"
                :searchPanel="{
                  visible: false,
                }"
                :pager="{ visible: false }"
                :ref="'master-details-' + lineItem.data.ID"
                :elementAttr="{ class: 'internal-grid' }"
                :focusedRowEnabled="true"
                :key-expr="'BatchLineNo'"
                @editing-start="onBatchEditingStart"
                @cell-prepared="onBatchCellPrepared"
                @row-prepared="onRowPrepared"
                @focused-cell-changed="onBatchFocusedCellChanged"
                @editor-preparing="onBatchEditorPreparing"
                :on-row-removed="onBatchRowRemoved"
              >
                <DxEditing
                  :allow-updating="baseView.mode !== 'review'"
                  :allow-deleting="baseView.mode !== 'review'"
                  :new-row-position="'last'"
                  :select-text-on-edit-start="true"
                  mode="cell"
                />

                <template #Decimal="{ data }">
                  <GcDecimal :dataValue="data" :format="data.column.format" />
                </template>
              </dx-data-grid>
            </template>
          </dx-data-grid>
        </div>
        <GcFooter v-if="showFooter && loaded">
          <template #leftContent>
            <div
              class="gc-grid-footer-item left-content"
              v-for="item in footerLeftItems"
              :key="item.label"
            >
              <label class="gc-footer-label">{{ item.label }}</label>

              <label
                v-if="item.fieldType === 'custome-string'"
                class="gc-footer-value"
                >{{ item.value }}</label
              >
              <GcFooterItem
                :value="item.value"
                :format="item.format"
                :length="item.format === 'Decimal-16-4C' ? 8 : 4"
                v-else
              />
            </div>
          </template>
          <template #rightContent>
            <div
              class="gc-grid-footer-item"
              v-for="item in footerRightItems"
              :key="item.label"
            >
              <label class="gc-footer-label">{{ item.label }}</label>
              <GcFooterItem
                :value="item.value"
                :format="item.format"
                :length="item.format === 'Decimal-16-4C' ? 8 : 6"
              />
            </div>
          </template>
        </GcFooter>
        <!-- Product Search Popup  -->
        <GcPopUpSearchForItem
          v-if="searchForItem && searchForItem.visible"
          :setVisible="searchForItem.visible"
          :callBack="searchForItem.popUpCallBack"
          :baseView="baseView"
          :setLoading="showLoading"
          :productListQuery="
            searchForItem.productListQuery
              ? searchForItem.productListQuery
              : undefined
          "
          :productListColumns="
            searchForItem.columns ? searchForItem.columns : undefined
          "
          :productListItems="
            searchForItem.productListItems
              ? searchForItem.productListItems
              : undefined
          "
          :title="'Search Product'"
          :gridOptions="{
            options: {
              pageSize: 25,
            },
          }"
        />
      </div>
    </div>
  </GcBaseLayout>
</template>

<script>
import { groupByAndSum } from "@/_helpers/collections/array-helper";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";
import { FeatureStorage } from "../../_services/storage";
import FooterItemConfig from "@/_helpers/data-grid/footer-items";
import {
  backwardTabbing,
  forwardTabbing,
  onEnter,
} from "@/_helpers/ui/keyboard";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import FormMixin from "@/_mixins/form.mixin";
import RouteMixin from "@/_mixins/route.mixin";
import { StockAdjustmentBatchService } from "@/_services/api/inventory/stock-adjustment-batch.service";
import { StockAdjustmentReviewService } from "@/_services/api/inventory/stock-adjustment-review.service";
import { StockAdjustmentService } from "@/_services/api/inventory/stock-adjustment.service";
import { ProductService } from "@/_services/api/products/product.service";
import { BranchService } from "@/_services/api/sales/branch.service";
import { getFactorByCode } from "@/_store/helpers";
import { DxMasterDetail } from "devextreme-vue/data-grid";
import ArrayStore from "devextreme/data/array_store";
import DataGrid from "devextreme/ui/data_grid";
import { mapGetters } from "vuex";
import { ProductSupplierService } from "@/_services/api/products/product-supplier.service";

const { getFooterLeftItems, getFooterRightItems } = FooterItemConfig;

let productItemBase = {
  ID: 1,
  Code: null,
  Description1: undefined,
  IsBatchProduct: "No",
  IsVisibleBatchData: false,
  BranchCode: undefined,
  BranchDescription: undefined,
  UnitOfMeasureCode: undefined,
  StandardCost: 0,
  OnHand: 0,
  NewQuantity: 0,
  AdjustmentQuantity: 0,
  AdjustedQty: 0,
  AdjustmentValue: 0,
  Comment: "",
  isValid: true,
  ChildDataSource: new ArrayStore({
    key: "BatchLineNo",
    data: [],
  }),
};

let validationErrorExists = false;
// eslint-disable-next-line no-unused-vars
let batchItemBase = {
  BatchNo: "",
  SupplierBatchNo: "",
  SupplierCode: "",
  OnHand: 0,
  Available: 0,
  NewQuantity: 0,
  AdjustmentQuantity: 0,
  SupplierOutstandingQuantity: 0,
  IsManufacturedDateRequired: 0,
  UseByShelfLife: 0,
  BestBeforeShelfLife: 0,
  IsBatchProduct: undefined,
  UseByDate: undefined,
  BestBeforeDate: undefined,
  ManufacturedDate: undefined,
  ParentID: null,
  BatchLineNo: 0,
  FromBatchDB: false,
  isValid: true,
};

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: { DxMasterDetail },
  data() {
    return {
      detailKey: 0,
      loaded: false,
      showFooter: true,
      ConsumeProductBatchFilter: [],
      model: {
        DateIn: new Date(),
        DateReceived: new Date(),
        toFriendlyStatus: "N",
        Status: "N",
      },
      dataGridLocalItemDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      ui: {
        DateAdjustedMin: null,
        DateAdjustedMax: null,
      },
      stockEnquiryDictionary: {},
      productDataSourceItems: [],
      branchDataSourceItems: [],
      defaultBranchCode: undefined,
      validationColumn: undefined,
      validationDataRowKey: undefined,
      searchForItem: {},
      batchDataEditingCell: undefined,
      dataEditingCell: undefined,
      openBatchDataGrid: false,
      changes: [],
      footerItems: [],
      batchNoTabKey: false,
      expandChildGrid: true,
      IsBatchAvailable: false,
      BatchGridInstance: undefined,
      selectedBatch: null,
      selectedSupplier: null,
      isDisplayError: false,
    };
  },
  async created() {},
  async mounted() {
    this.showLoading(true);
    await this.initialized();
  },
  beforeDestroy() {
    this.$store.dispatch("dataContext/setSearchText", undefined);
  },

  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    ...mapGetters({ headerFormHeight: "ui/getHeaderFormHeight" }),
    ...mapGetters({ gridExpand: "ui/getGridExpand" }),
    ...mapGetters({
      getValidationSummaryVisible: "ui/getValidationSummaryVisible",
    }),
    dataGrid() {
      const dataGrid = this.$refs["dx-data-grid-comp"];
      if (dataGrid) {
        return dataGrid.instance;
      }
      return undefined;
    },
    getGridHeight() {
      return this.gridHeight - 116;
    },
    backButtonRoute() {
      return "/stock-adjustment";
    },
    getScreenJson() {
      return {
        title: "Screen Name",
      };
    },
    getHeaderScreenJson() {
      return {
        headerFormVisible: true,
        headerToolbar: {
          audit: this.baseView.mode === "review",
          status: true,
          save: this.baseView.mode === "create",
          saveCallback: this.saveStockAdjustment,
          pdf: this.baseView.mode === "review",

          toolbarFormItems: [
            {
              dataField: "DateIn",
              editorType: "dxDateBox",
              label: {
                text: "Date Adjusted",
              },
              editorOptions: {
                displayFormat: "dd MMM yyyy",
                width: 120,
                min: this.ui.DateAdjustedMin,
                max: this.ui.DateAdjustedMax,
                readOnly: this.baseView.mode === "review",
                onKeyDown: this.onDateInKeyDown,
              },
            },
          ],
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {},
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {},
                },
              ],
            },
          ],
          ellipsis: {
            visible:
              FeatureStorage.checkPermission("StockAdjustment", "create") &&
              this.baseView.mode === "review",
            menuItems: [
              {
                menuText: "Copy",
                visible: this.baseView.mode === "review",
              },
              {
                menuText: "Reverse",
                visible:
                  this.model.Status == "Completed" &&
                  this.baseView.mode === "review",
              },
            ],
            clickAction: (e) => {
              if (e.itemData.menuText === "Copy") {
                this.onCopyClickHandle();
              }
              if (e.itemData.menuText === "Reverse") {
                this.onReverseClickHandle();
              }
            },
          },
          customStatus: {
            enabled: true,
            key: "CustomStatus",
          },
        },
      };
    },
    getProductEntryColumns() {
      return [
        {
          caption: "Product",
          dataField: "Code",
          allowSearch: false,
          allowSorting: false,
          allowEditing: this.baseView.mode !== "review",
          width: ColumnWidth.PurchaseOrderCode.width,
          maxWidth: ColumnWidth.PurchaseOrderCode.minWidth,
          cssClass: "edit-cell-textbox-field",
          editorOptions: {
            dataSource: ProductService.dataSourceConfig("api/Product", {
              filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
              select: [
                "Code",
                "Description1",
                "Inactive",
                "IsBatchProduct",
                "UnitOfMeasureCode",
                "IsManufacturedDateRequired",
                "StandardCost",
                "StockEnquiryQuantities",
                "StockTransferUnitOfMeasureList",
                "BestBeforeShelfLife",
                "UseByShelfLife",
              ],
              expand:
                "StockTransferUnitOfMeasureList($select=UnitOfMeasure,BranchCode,ConversionFactor,DefaultUnitOfMeasure,DefaultUnitOfMeasureConversion,StandardCost)",
              sort: "Code asc",
            }),
            displayExpr: "Code",
            valueExpr: "Code",
            searchExpr: ["Code", "Description1"],
            searchTimeout: 500,
            searchEnabled: true,
            onKeyDown: this.onEnterProductCode,
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.Code}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.Description1}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 400,
            },
            onItemClick: (e, lineData) => {
              // update e.event.key to "Enter" to simulate enter key press
              e.event.key = "Enter";
              this.onEnterProductCode(e, lineData);
            },
            onSelectionChanged: this.onProductCodeSelected,
            onOpened: (e) => {
              if (e) e.component.getDataSource().reload();
            },
          },
          fixed: true,
          fixedPosition: "left",
          setCellValue: this.onSetProduct,
        },
        {
          caption: "Description",
          dataField: "Description1",
          allowEditing: false,
          minWidth: ColumnWidth.ProductDescription.minWidth,
        },
        {
          caption: "UM",
          dataField: "SalesUM",
          allowEditing: this.baseView.mode !== "review",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-textbox-field-1 gc-editable-cell-color"
              : undefined,
          editCellTemplate:
            this.baseView.mode != "review" ? "sales-um-edit-cell-template" : "",
          // eslint-disable-next-line no-unused-vars
          setCellValue: async (newData, value, _currentRowData) => {
            newData.SalesUM = value;
          },
          width: ColumnWidth.UnitOfMeasure.width,
          maxWidth: ColumnWidth.UnitOfMeasure.minWidth,
        },
        {
          caption: "Branch",
          dataField: "BranchCode",
          width: ColumnWidth.BranchCode.width,
          maxWidth: ColumnWidth.BranchCode.width,
          cssClass:
            this.baseView.mode === "review" ? "" : "edit-cell-select-field",
          allowEditing: this.baseView.mode !== "review",
          allowSorting: false,
          lookup:
            this.baseView.mode !== "review"
              ? {
                  allowClearing: false,
                  dataSource: this.branchDataSourceItems,
                  displayExpr: "Code",
                  valueExpr: "Code",
                }
              : undefined,
          editorOptions: {
            searchExpr: ["Code", "Description"],
            searchTimeout: 1,
            dropDownOptions: {
              minWidth: 200,
              width: "auto",
            },
            itemTemplate: "select-box-item-template",
            onKeyDown: this.onEnterBranchCode,
            onFocusIn: (e) => {
              e.event.target.select();
              this.clearBatchGridSelection();
            },
            onItemClick: (e, lineData) => {
              this.onEnterBranchCode({ event: { key: "Enter" } }, lineData);
            },
          },
          setCellValue: this.onSetBranchCode,
        },
        {
          caption: "Unit Cost",
          dataField: "StandardCost",
          allowEditing: false,
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          cssClass: "decimal-cell",
        },
        {
          caption: "On Hand",
          dataField: "OnHand",
          allowEditing: false,
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          cssClass: "decimal-cell",
          visible: this.baseView.mode !== "review",
        },
        {
          caption: "New Quantity",
          dataField: "NewQuantity",
          allowEditing: this.baseView.mode !== "review",
          cssClass: "edit-cell-numberbox-field",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
              this.clearBatchGridSelection();
            },
            onKeyDown: (e, lineData) => {
              setTimeout(() => {
                this.moveOnEnterKey(e, lineData);
              }, 50);
            },
            format: "##,###,###,###,##0.0000",
            max: 999999999.0,
            step: 0,
          },
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          setCellValue: this.onNewQuantityChangeHandler,
          visible: this.baseView.mode !== "review",
        },
        {
          caption: "Adjustment",
          dataField: "AdjustmentQuantity",
          allowEditing: this.baseView.mode !== "review",
          cssClass: "edit-cell-numberbox-field",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
              this.clearBatchGridSelection();
            },
            onKeyDown: this.moveOnEnterKey,
            format: "##,###,###,###,##0.0000",
            max: 999999999.0,
            step: 0,
          },
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          setCellValue: this.onAdjustmentQtyChangeHandler,
          visible: this.baseView.mode !== "review",
        },
        {
          caption: "Adjustment",
          dataField: "AdjustedQty",
          format: "##,###,###,###,##0.0000",
          visible: this.baseView.mode === "review",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
        },
        {
          caption: "Adjustment Value",
          dataField: "AdjustmentValue",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        {
          caption: "Comments",
          dataField: "Comment",
          allowEditing: this.baseView.mode !== "review",
          cssClass: "edit-cell-textbox-field",
          allowSearch: false,
          allowSorting: false,
          width: 600,
          maxWidth: 600,
          editorOptions: {
            maxLength: 1000,
            onFocusIn: (e) => {
              e.event.target.select();
              this.clearBatchGridSelection();
            },
            onKeyDown: this.moveOnEnterKey,
          },
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          buttons: [
            {
              name: "add",
              icon: "add",
              onClick: (e) => {
                this.addNewRowByPosition(e, "add-line");
              },
            },
            {
              name: "delete",
            },
          ],
          visible: this.baseView.mode !== "review",
        },
      ];
    },
    getFooterItems() {
      return [
        {
          screenField: "SalesUMItemCount",
          dataField: "SupplierRequired",
          location: "left",
          label: "Line Count",
          value: 0,
          customCalc: (headerData, gridItems) => {
            let count = 0;
            gridItems.map((element) => {
              if (element.Code) count++;
            });

            return count;
          },
        },
        {
          screenField: "SalesUMItemCount",
          dataField: "AdjustedQty",
          location: "left",
          label: "Adjustment Qty",
          format: "Decimal-16-2",
          value: 0,
        },
        {
          screenField: "SalesUMItemCount",
          dataField: "AdjustmentValue",
          location: "right",
          label: "Total Adjustment Value",
          format: "Decimal-16-2C",
          value: 0,
        },
      ];
    },
    footerLeftItems() {
      return getFooterLeftItems(this.footerItems, this.model);
    },
    footerRightItems() {
      return getFooterRightItems(this.footerItems, this.model);
    },
    getLabel() {
      return this.expandChildGrid ? "Collapse" : "Expand";
    },
  },
  methods: {
    childGridKeyDown(e, data, lineItem) {
      if (data.column.editorOptions && data.column.editorOptions.onKeyDown) {
        let func = data.column.editorOptions.onKeyDown;
        func(e, data, lineItem);
      }
    },
    onCopyClickHandle() {
      this.$router
        .push({
          path: `/stock-adjustment-v2/create/${this.baseView.id}`,
          query: {
            copy: true,
          },
        })
        .then(() => {
          this.showLoading(true);
          this.refreshView();
          this.loaded = false;
          this.initialized();
        });
    },
    async showReverseConfirmation(Code) {
      return this.$confirm(
        `Are you sure you want to reverse this Stock Adjustment #${Code}? This action cannot be undone.`,
        "Reverse Stock Adjustment",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          type: "warning",
        },
      );
    },
    async onReverseClickHandle() {
      const confirmLeaving = await this.showReverseConfirmation(
        this.baseView.id,
      );
      if (confirmLeaving) {
        this.loaded = true;
        this.showLoading(true);
        await StockAdjustmentService.reverse(this.baseView.id)
          .then((res) => {
            this.$notify(
              `Reversal of Stock Adjustment #${this.baseView.id} is successful`,
              "success",
            );
            //
            if (res) this.showLoading(false);
            this.refreshView();
            this.loaded = false;
            this.initialized();
          })
          .catch(async (err) => {
            this.showLoading(false);
            this.initialized();
            this.$alert(
              err.data[0].ErrorMessage,
              "Reverse Stock Adjustment Failed",
            );
          });
      }
    },
    /**
     *
     * @param adjustmentQty
     * @param AvailableQuantity
     * @param column
     * @param rowIndex
     * @returns {Promise<void>}
     */
    async showBatchAlertPositive(
      adjustmentQty,
      AvailableQuantity,
      column,
      rowIndex = 0,
    ) {
      await this.$alert(
        `You have entered an Adjustment Qty of ${adjustmentQty}, which will result in the New Qty being less than ${AvailableQuantity}. Please update the Adjustment Qty so that the New Qty is greater than or equal to ${AvailableQuantity}`,
        "Validation Error",
      );
      this.focusOnBatchEditableCell(rowIndex, column);
    },
    /**
     *
     * @param adjustmentQty
     * @param availableQuantity
     * @param column
     * @param rowIndex
     * @returns {Promise<void>}
     */
    async showBatchAlertNegative(
      adjustmentQty,
      availableQuantity,
      column,
      rowIndex = 0,
    ) {
      await this.$alert(
        `You have entered an Adjustment Qty of ${adjustmentQty}, which will result in the New Qty being less than ${availableQuantity}. Please update the Adjustment Qty so that the New Qty is greater than ${availableQuantity}`,
        "Validation Error",
      );
      this.focusOnBatchEditableCell(rowIndex, column);
    },
    /**
     *
     * @param rowIndex
     * @param column
     */
    focusOnBatchEditableCell(rowIndex, column = "BatchNo") {
      const batchDataGrid = this.BatchGridInstance;
      const cellElement = batchDataGrid.getCellElement(rowIndex, column);
      batchDataGrid.focus(cellElement);

      setTimeout(() => {
        batchDataGrid.editCell(rowIndex, column);
      }, 10);
    },
    /**
     *
     * @returns {dxDataGrid}
     */
    getDataGridInstance() {
      return this.$refs["dx-data-grid-comp"].instance;
    },
    /**
     *
     * @param id
     * @returns {*}
     */
    getMasterDataInstance(id) {
      return this.$refs["master-details-" + id].instance;
    },
    /**
     *
     * @param data
     * @returns {*}
     */
    getChildGridColumns(data) {
      // Width of the viewport
      const viewportWidth = window.innerWidth;

      let additionalSize = 0;
      let additionalSupplierCodeSize = 0;
      if (viewportWidth >= 1698) {
        additionalSize = 360;
        additionalSupplierCodeSize = 35;
      }

      let item = data.data;

      let visibleUseBy = true;
      let visibleBestBefore = true;
      // let changeWidth = 0;

      if (item.UseByShelfLife === -1 && item.BestBeforeShelfLife === -1) {
        visibleUseBy = false;
        visibleBestBefore = false;
        // changeWidth = ColumnWidth.Date.width / 2;
      } else if (item.UseByShelfLife > -1 && item.BestBeforeShelfLife > -1) {
        visibleBestBefore = false;
      } else if (
        item.UseByShelfLife > -1
        // && item.BestBeforeShelfLife != -1
      ) {
        visibleBestBefore = false;
      } else if (
        item.BestBeforeShelfLife > -1
        // && item.UseByShelfLife != -1
      ) {
        visibleUseBy = false;
      }
      if (
        (this.baseView.mode === "review" ||
          (this.baseView.mode == "create" &&
            this.baseView.query.copy === "true")) &&
        item.ChildDataSource._array.length > 0
      ) {
        if (item.ChildDataSource._array[0].BestBeforeDate === null) {
          visibleBestBefore = false;
        }
        if (item.ChildDataSource._array[0].UseByDate === null) {
          visibleUseBy = false;
        }
      }

      // let manufacturedDateCss = "edit-cell-datebox-field-border";
      // if (
      //     item.IsManufacturedDateRequired === "Y"
      // ) {
      //   manufacturedDateCss = `${manufacturedDateCss} gc-require-validation`;
      // }

      return this.parseColumnWithScreenFields([
        {
          screenField: "Batch",
          dataField: "BatchNo",
          allowEditing: item.IsBatchProduct !== "No",
          showEditorAlways: false,
          width: ColumnWidth.BatchNo.width - 20,
          minWidth: ColumnWidth.BatchNo.minWidth - 20,
          cssClass: "edit-cell-textbox-field",
          editorOptions: {
            dataSource: ProductService.dataSourceConfig(
              "api/ConsumeProductBatch",
              {
                filter: [
                  ["ItemCode", "=", item.Code],
                  ["BranchCode", "=", item.BranchCode],
                  ["SourceTransactionType", "=", ""],
                  ["SourceTransactionNo", "=", 0],
                  ["SourceTransactionLine", "=", 0],
                  ["SourceTransactionProcessCode", "=", ""],
                  // ["AvailableQty", ">", 0],
                ],
                select: [
                  "BatchNo",
                  "BranchCode",
                  "SupplierCode",
                  "SupplierBatchNo",
                  "ManufacturedDate",
                  "UseByDate",
                  "BestBeforeDate",
                  "AvailableQty",
                  "OnHand",
                ],
                sort: "UseByDateForSort asc,BestBeforeDateForSort asc,DateCreated asc",
              },
            ),
            displayExpr: "BatchNo",
            valueExpr: "BatchNo",
            searchTimeout: 10,
            searchEnabled: true,
            itemTemplate: (data) => {
              let secondary = data.SupplierBatchNo;
              if (data.UseByDate) secondary += " | " + data.UseByDate;
              else {
                if (data.BestBeforeDate)
                  secondary += " | " + data.BestBeforeDate;
              }
              secondary += " | " + data.AvailableQty;

              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.BatchNo}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${secondary}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 400,
            },
            acceptCustomValue: true,
            onCustomItemCreating: this.onCustomItemCreating,
            onSelectionChanged: (e) => (this.selectedBatch = e.selectedItem),
            onKeyDown: this.onBatchNoKeyDown,
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onOpened: (e) => {
              if (e) e.component.getDataSource().reload();
            },
          },
          setCellValue: this.onBatchSet,
        },
        {
          caption: "Supplier Code",
          dataField: "SupplierCode",
          allowEditing: item.IsBatchProduct !== "No",
          allowSearch: false,
          allowSorting: false,
          showEditorAlways: false,
          width:
            this.baseView.mode !== "review"
              ? 248 + additionalSupplierCodeSize
              : ColumnWidth.SupplierRef.width + 63,
          minWidth: ColumnWidth.SupplierRef.minWidth,
          cssClass: "edit-cell-textbox-field",
          editorOptions: {
            dataSource: ProductService.dataSourceConfig(
              "api/ProductSupplierList",
              {
                filter: [["ProductCode", "=", item.Code]],
                select: ["SupplierCode", "SupplierName"],
                // sort: "Name asc",
              },
            ),
            displayExpr: "SupplierCode",
            valueExpr: "SupplierCode",
            searchExpr: ["SupplierCode", "SupplierName"],
            searchTimeout: 10,
            searchEnabled: true,
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.SupplierCode}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.SupplierName}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 400,
            },
            // acceptCustomValue: true, // User can not add new suppliers via batch grid. - SD
            onCustomItemCreating: (e) => {
              e.customItem = {
                SupplierCode: e.text,
              };
            },
            onSelectionChanged: (e) => (this.selectedSupplier = e.selectedItem),
            // onKeyDown: this.onBatchNoKeyDown,
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onFocusOut: (e, lineData) => {
              setTimeout(async () => {
                if (
                  typeof lineData.data.data.BatchNo !== "undefined" &&
                  lineData.data.data.BatchNo !== "null" &&
                  lineData.data.data.BatchNo !== "" &&
                  !this.isDisplayError
                ) {
                  const currentProduct =
                    this.dataGridLocalItemDataSource._array.find(
                      (line) => line.ID === lineData.data.data.ParentID,
                    );
                  if (currentProduct) {
                    const SupplierList =
                      await ProductSupplierService.getSupplierByProductCode(
                        currentProduct.Code,
                      );
                    if (SupplierList.data.value.length > 0) {
                      const currentSupplier = SupplierList.data.value.find(
                        (item) =>
                          item.SupplierCode === lineData.data.data.SupplierCode,
                      );
                      if (!currentSupplier) {
                        await this.showSupplierCodeAlert(
                          lineData.data.data,
                          currentProduct,
                        );
                      }
                    } else {
                      await this.showSupplierCodeAlert(
                        lineData.data.data,
                        currentProduct,
                      );
                    }
                  }
                }
              }, 10);
            },
            // User can not add new suppliers via batch grid. Therefore this validation is not required. - SD
            onKeyDown: async (e, lineData) => {
              if (lineData) {
                if (onEnter(e) || forwardTabbing(e)) {
                  // setTimeout(async () => {
                  //   if(typeof lineData.data.data.SupplierCode !== "undefined" && lineData.data.data.SupplierCode !== "null" && lineData.data.data.SupplierCode !== ""){
                  //     const currentProduct =
                  //         this.dataGridLocalItemDataSource._array.find(
                  //             (line) => line.ID === lineData.data.data.ParentID
                  //         );
                  //     if (currentProduct) {
                  //       const SupplierList =
                  //           await ProductSupplierService.getSupplierByProductCode(
                  //               currentProduct.Code
                  //           );
                  //       if (SupplierList.data.value.length > 0) {
                  //         const currentSupplier = SupplierList.data.value.find(
                  //             (item) =>
                  //                 item.SupplierCode === lineData.data.data.SupplierCode
                  //         );
                  //         if (!currentSupplier) {
                  //           await this.showSupplierCodeAlert(
                  //               lineData.data.data,
                  //               currentProduct
                  //           );
                  //         }
                  //       } else {
                  //         await this.showSupplierCodeAlert(
                  //             lineData.data.data,
                  //             currentProduct
                  //         );
                  //       }
                  //     }
                  //   }
                  // }, 0)
                }
              }
            },
          },
          // validationRules: [{ type: 'required', message: 'Supplier Code is required' }],
          setCellValue: this.onBatchSupplierSet,
        },
        {
          screenField: "SupplierBatchNo",
          cssClass: "edit-cell-textbox-field",
          allowEditing: true,
          width:
            ColumnWidth.SupplierBatchNo.width +
            (this.baseView.mode !== "review" ? 6 : 105 + additionalSize),
          minWidth:
            ColumnWidth.SupplierBatchNo.minWidth +
            (this.baseView.mode !== "review" ? 6 : 105),
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            // onKeyDown: this.onSupplierBatchNoKeyDown,
          },
        },
        {
          caption: "On Hand",
          dataField: "OnHand",
          allowEditing: false,
          type: Number,
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Quantity2DecimalPoints.width + 4,
          maxWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          cssClass: "decimal-cell",
          visible: this.baseView.mode !== "review",
        },
        {
          caption: "Available",
          dataField: "Available",
          allowEditing: false,
          type: Number,
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          cssClass: "decimal-cell",
          visible: this.baseView.mode !== "review",
        },
        {
          caption: "New Quantity",
          dataField: "NewQuantity",
          allowEditing: this.baseView.mode !== "review",
          cssClass: "edit-cell-numberbox-field",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            // onFocusIn: (e) => {
            //   e.event.target.select();
            //   this.clearBatchGridSelection();
            // },
            onKeyDown: (e, lineData) => {
              if (lineData.data.data.FromBatchDB && backwardTabbing(e)) {
                e.event.preventDefault();
                this.focusOnBatchEditableCell(
                  lineData.data.rowIndex,
                  "BatchNo",
                );
              }
            },
            format: "##,###,###,###,##0.0000",
            max: 999999999.0,
            step: 0,
          },
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          setCellValue: this.onBatchNewQuantityChangeHandler,
          visible: this.baseView.mode !== "review",
        },
        {
          caption: "Adjustment",
          dataField: "AdjustmentQuantity",
          allowEditing: this.baseView.mode !== "review",
          cssClass: "edit-cell-numberbox-field",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            // onFocusIn: (e) => {
            //   e.event.target.select();
            //   this.clearBatchGridSelection();
            // },
            // onKeyDown: (e, lineData) => {
            //   setTimeout(() => {
            //     this.moveOnEnterKey(e, lineData);
            //   }, 50);
            // },
            format: "##,###,###,###,##0.0000",
            max: 999999999.0,
            step: 0,
          },
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          setCellValue: this.onBatchAdjustmentQtyChangeHandler,
          visible: true,
        },
        {
          screenField: "DateIn",
          caption: "Manufactured Date",
          dataField: "ManufacturedDate",
          allowEditing: true,
          allowSearch: false,
          dataType: "date",
          editable: true,
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          format: "dd MMM yyyy",
          cssClass: "edit-cell-datebox-field-border",
          showEditorAlways: false,
          // cssClass: manufacturedDateCss,
          // cellTemplate: (cellElement, cellInfo) => {
          //   const date = cellInfo.value;
          //   cellElement.classList.add('gc-require-validation');
          //   cellElement.textContent = new Date(date).toShortDateString();
          // },
          editorOptions: {
            format: "dd MMM yyyy",
            onKeyDown: this.onManufacturedDateKeyDown,
          },
          setCellValue: (newData, value, currentRowData) => {
            if (
              currentRowData.IsManufacturedDateRequired === "Y" &&
              value === ""
            ) {
              this.$alert("Manufactured Date is required", "Validation Error");
            }
            newData.ManufacturedDate = value;
            if (value) {
              if (
                currentRowData.UseByShelfLife > -1 &&
                currentRowData.BestBeforeShelfLife > -1
              ) {
                newData.UseByDate = new Date(value).addDays(
                  currentRowData.UseByShelfLife,
                );
              } else if (
                currentRowData.UseByShelfLife > -1
                // && item.BestBeforeShelfLife != -1
              ) {
                newData.UseByDate = new Date(value).addDays(
                  currentRowData.UseByShelfLife,
                );
              } else if (
                currentRowData.BestBeforeShelfLife > -1
                // && item.UseByShelfLife != -1
              ) {
                newData.BestBeforeDate = new Date(value).addDays(
                  currentRowData.BestBeforeShelfLife,
                );
              } else {
                newData.UseByDate = undefined;
                newData.BestBeforeDate = undefined;
              }
            } else {
              newData.UseByDate = undefined;
              newData.BestBeforeDate = undefined;
            }
            const index = this.BatchGridInstance.getRowIndexByKey(
              newData.BatchLineNo,
            );
            this.BatchGridInstance.repaintRows([index]);
          },
        },
        {
          screenField: "UseByDate",
          allowEditing: true,
          allowSearch: false,
          showEditorAlways: false,
          cssClass: "edit-cell-datebox-field-border",
          dataType: "date",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          visible: visibleUseBy,
          format: "dd MMM yyyy",
          editorOptions: {
            format: "dd MMM yyyy",
            // onKeyDown: this.onUseByDateKeyDown,
          },
        },
        {
          screenField: "BestBeforeDate",
          allowEditing: true,
          allowSearch: false,
          showEditorAlways: false,
          cssClass: "edit-cell-datebox-field-border",
          dataType: "date",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          visible: visibleBestBefore,
          format: "dd MMM yyyy",
          editorOptions: {
            format: "dd MMM yyyy",
            // onKeyDown: this.onUseByDateKeyDown, // UseByDate & BestBeforeDate Both acts in the same way
          },
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          width: 110,
          buttons: [
            {
              hint: "Add Batch Line",
              icon: "add",
              visible: true,
              onClick: this.onAddNewBatchLine.bind(this),
            },
            "delete",
          ],
          visible: this.baseView.mode !== "review",
        },
      ]);
    },

    async onManufacturedDateKeyDown(e, lineData) {
      if (this.BatchGridInstance && (forwardTabbing(e) || backwardTabbing(e))) {
        setTimeout(async () => {
          this.BatchGridInstance.byKey(lineData.data.data.BatchLineNo).then(
            async (data) => {
              if (
                data.IsManufacturedDateRequired === "Y" &&
                !data.ManufacturedDate
              ) {
                await this.$alert(
                  "Manufactured Date is required",
                  "Validation Error",
                );
                const rowIndex = this.BatchGridInstance.getRowIndexByKey(
                  data.BatchLineNo,
                );
                setTimeout(() => {
                  this.focusOnBatchEditableCell(rowIndex, "ManufacturedDate");
                  this.isDisplayError = false;
                }, 10);
              }
            },
          );
        }, 1);
      }
    },

    async showSupplierCodeAlert(lineData, currentProduct) {
      this.isDisplayError = true;
      await this.$alert(
        `The supplier provided is not a valid supplier for ${currentProduct.Code} - ${currentProduct.Description1}. Please select a valid supplier for this item or add a new supplier to the Product before proceeding with this stock adjustment.`,
      );
      const rowIndex = this.BatchGridInstance.getRowIndexByKey(
        lineData.BatchLineNo,
      );
      setTimeout(() => {
        this.focusOnBatchEditableCell(rowIndex, "SupplierCode");
        this.isDisplayError = false;
      }, 10);
    },
    /**
     *
     */
    onAddNewBatchLine(e) {
      if (e.event) {
        let data = e.row.data;
        if (!data.BatchNo) {
          this.$alert("Batch No is required", "Validation Error");
        }
        // else if (
        //   data.IsManufacturedDateRequired === "Y" &&
        //   !data.ManufacturedDate
        // ) {
        //   this.$alert("Manufactured Date is required", "Validation Error");
        // }
        else if (!data.SupplierCode) {
          this.$alert("Supplier Code is required", "Validation Error");
        } else {
          let instance = this.getDataGridInstance();
          instance.byKey(data.ParentID).then((lineParent) => {
            this.addNewBatchLine(lineParent);
          });
        }
      }
    },
    /**
     *
     * @param lineParent
     */
    getMaxLineNo(lineParent) {
      let maxLineNo = Math.max(
        ...lineParent.ChildDataSource._array.map((e) => e.BatchLineNo),
      );
      if (lineParent.ChildDataSource._array.length === 0)
        maxLineNo = parseInt(lineParent.ID.toString() + "0") * 100;
      return ++maxLineNo;
    },
    addNewBatchLine(lineParent, focusByDefault = true) {
      if (!lineParent.ChildDataSource) {
        lineParent.ChildDataSource = new ArrayStore({
          key: "BatchLineNo",
          data: [],
        });
      }

      const batchItem = { ...batchItemBase };
      batchItem.ParentID = lineParent.ID;
      batchItem.BatchLineNo = this.getMaxLineNo(lineParent);
      batchItem.BestBeforeShelfLife = lineParent.BestBeforeShelfLife;
      batchItem.IsManufacturedDateRequired =
        lineParent.IsManufacturedDateRequired;
      batchItem.SupplierCode = lineParent.SupplierCode;
      batchItem.UseByShelfLife = lineParent.UseByShelfLife;
      batchItem.TransactionUMConversion = lineParent.TransactionUMConversion;
      lineParent.ChildDataSource.insert(batchItem);

      this.dataGridLocalItemDataSource.update(lineParent.ID, lineParent);
      setTimeout(() => {
        this.BatchGridInstance.saveEditData();
        this.BatchGridInstance.refresh(true);
        setTimeout(() => {
          const batchIndex = this.BatchGridInstance.getRowIndexByKey(
            batchItem.BatchLineNo,
          );
          if (batchIndex !== -1 && focusByDefault) {
            this.focusOnBatchEditableCell(batchIndex);
          }
        }, 50);
      }, 250);
    },
    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     */
    onBatchNewQuantityChangeHandler(newData, value, currentRowData) {
      newData.TransactionUMConversion = currentRowData.TransactionUMConversion;
      newData.IsValid = true;
      if (
        this.batchValidation(
          currentRowData.AdjustmentQuantity,
          value,
          currentRowData,
          "NewQuantity",
        )
      ) {
        newData.NewQuantity = value;
        this.setStockUMQuantity(newData, "NewQuantity");
        newData.AdjustmentQuantity = value - currentRowData.Available;
        this.setStockUMQuantity(newData, "AdjustmentQuantity");
      }
      setTimeout(() => this.updateParent(currentRowData.ParentID), 200);
      const index = this.BatchGridInstance.getRowIndexByKey(
        currentRowData.BatchNo,
      );
      this.BatchGridInstance.repaintRows([index]);
    },
    /**
     *
     * @param AdjustmentQuantity
     * @param NewQuantity
     * @param currentRowData
     * @param column
     * @returns {boolean}
     */
    batchValidation(AdjustmentQuantity, NewQuantity, currentRowData, column) {
      validationErrorExists = false;
      const batchDataGrid = this.BatchGridInstance;
      if (currentRowData.Available > 0) {
        if (NewQuantity < 0) {
          this.showBatchAlertPositive(
            AdjustmentQuantity,
            currentRowData.Available,
            column,
            batchDataGrid.getRowIndexByKey(currentRowData.BatchLineNo),
          );
          validationErrorExists = true;
          return false;
        }
      } else {
        if (NewQuantity < currentRowData.Available) {
          this.showBatchAlertNegative(
            AdjustmentQuantity,
            currentRowData.Available,
            column,
            batchDataGrid.getRowIndexByKey(currentRowData.BatchLineNo),
          );
          validationErrorExists = true;
          return false;
        }
      }
      return true;
    },
    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     */
    onBatchAdjustmentQtyChangeHandler(newData, value, currentRowData) {
      newData.TransactionUMConversion = currentRowData.TransactionUMConversion;
      newData.IsValid = true;
      const newQuantity = currentRowData.Available + value;
      if (
        this.batchValidation(
          currentRowData.AdjustmentQuantity,
          newQuantity,
          currentRowData,
          -"AdjustmentQuantity",
        )
      ) {
        newData.NewQuantity = newQuantity;
        this.setStockUMQuantity(newData, "NewQuantity");
        newData.AdjustmentQuantity = value;
        this.setStockUMQuantity(newData, "AdjustmentQuantity");

        // set chlildgrid datasource
        const currentProduct = this.dataGridLocalItemDataSource._array.find(
          (product) => product.ID === currentRowData.ParentID,
        );
        if (currentProduct) {
          const batchIndex = currentProduct.ChildDataSource._array.findIndex(
            (batch) => batch.BatchLineNo === currentRowData.BatchLineNo,
          );
          if (batchIndex !== -1) {
            currentProduct.ChildDataSource._array[
              batchIndex
            ].TransactionUMConversion = currentRowData.TransactionUMConversion;
            currentProduct.ChildDataSource._array[batchIndex].NewQuantity =
              newQuantity;
            this.setStockUMQuantity(
              currentProduct.ChildDataSource._array[batchIndex],
              "NewQuantity",
            );
            currentProduct.ChildDataSource._array[
              batchIndex
            ].AdjustmentQuantity = value;
            this.setStockUMQuantity(
              currentProduct.ChildDataSource._array[batchIndex],
              "AdjustmentQuantity",
            );
          }
        }
        this.updateParent(currentRowData.ParentID);
      }
      setTimeout(() => this.updateParent(currentRowData.ParentID), 200);
      const index = this.BatchGridInstance.getRowIndexByKey(
        currentRowData.BatchNo,
      );
      this.BatchGridInstance.repaintRows([index]);
      this.nextParentFocus(currentRowData);
    },
    nextParentFocus(currentRowData) {
      if (this.tabKey) {
        this.tabKey = false;
        const currentProduct = this.dataGridLocalItemDataSource._array.find(
          (product) => product.ID === currentRowData.ParentID,
        );
        if (currentProduct) {
          const batchIndex = currentProduct.ChildDataSource._array.findIndex(
            (batch) => batch.BatchLineNo === currentRowData.BatchLineNo,
          );
          if (batchIndex !== -1) {
            const nextBatch =
              currentProduct.ChildDataSource._array[batchIndex + 1];
            if (nextBatch) {
              const nextIndex = this.BatchGridInstance.getRowIndexByKey(
                nextBatch.BatchLineNo,
              );
              this.focusOnBatchEditableCell(nextIndex, "BatchNo");
            } else {
              const dataGrid = this.dataGrid;
              if (dataGrid) {
                let nextIndex = dataGrid.getRowIndexByKey(
                  currentRowData.ParentID,
                );
                nextIndex = nextIndex + 2;
                this.focusOnEditableCell(nextIndex, "Code");
              }
            }
          }
        }
      }
    },
    /**
     *
     * @param e
     */
    onBatchFocusedCellChanged(e) {
      let gridInstance = this.getDataGridInstance();
      if (gridInstance) gridInstance.option("focusedRowIndex", -1);

      if (e) {
        this.BatchGridInstance = e.component;
      }
    },
    /**
     *
     * @param e
     */
    onBatchEditingStart(e) {
      let data = e.data;

      if (
        e.column.dataField === "ManufacturedDate" ||
        e.column.dataField === "SupplierCode" ||
        e.column.dataField === "SupplierBatchNo"
      ) {
        if (data.FromBatchDB) {
          e.cancel = true;
        }
      }

      if (
        e.column.dataField === "UseByDate" ||
        e.column.dataField === "BestBeforeDate"
      ) {
        if (!data.ManufacturedDate && data.FromBatchDB) {
          e.cancel = true;
        }
      }
    },
    /**
     *
     * @param e
     */
    onBatchCellPrepared(e) {
      if (e.rowType === "data") {
        if (e.column.dataField === "BatchNo") {
          if (e.data.IsBatchProduct !== "No" && !e.data.BatchNo) {
            e.cellElement.classList.add("gc-require-validation");
          }
        }

        if (e.column.dataField === "ManufacturedDate") {
          if (
            e.data.IsManufacturedDateRequired === "Y" &&
            !e.data.ManufacturedDate
          ) {
            e.cellElement.classList.add("gc-require-validation");
          }
        }

        if (e.column.dataField === "UseByDate" && e.data.UseByDate === null) {
          e.column.visible = false;
        }
        if (
          e.column.dataField === "BestBeforeDate" &&
          e.data.BestBeforeDate === null
        ) {
          e.column.visible = false;
        }
      }
    },
    /**
     *
     * @param e
     * @param cellInfo
     */
    onBatchNoKeyDown(e, cellInfo) {
      if (e.event) {
        let batchGridInstance = cellInfo.component;
        if (backwardTabbing(e)) {
          if (cellInfo.data.rowIndex === 0) {
            if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
            batchGridInstance.option("focusedRowIndex", -1);
            e.event.preventDefault();
            let data = cellInfo.data.data;
            let instance = this.getDataGridInstance();
            let parentRowIndex = instance.getRowIndexByKey(data.ParentID);

            this.focusOnEditingCell(parentRowIndex, "Comment");
          }
        } else if (forwardTabbing(e)) {
          if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
          batchGridInstance.option("focusedRowIndex", -1);
          this.batchNoTabKey = true;

          if (cellInfo.data.data.FromBatchDB) {
            e.event.preventDefault();
            let item = cellInfo.data.data;
            if (batchGridInstance) {
              let cellElement = batchGridInstance.getCellElement(
                0,
                "NewQuantity",
              );
              let rowIndex = batchGridInstance.getRowIndexByKey(
                item.BatchLineNo,
              );
              if (cellElement) {
                batchGridInstance.focus(cellElement);
                setTimeout(() => {
                  batchGridInstance.editCell(rowIndex, "NewQuantity");
                }, 1);
              }
            }
          }
        }
      }
    },
    /**
     *
     * @param rowIndex
     * @param dataField
     */
    focusOnEditingCell(rowIndex, dataField) {
      setTimeout(() => {
        let gridInstance = this.getDataGridInstance();
        if (gridInstance) {
          let cellElement = gridInstance.getCellElement(rowIndex, dataField);
          if (cellElement) {
            gridInstance.focus(cellElement);
            setTimeout(() => {
              gridInstance.editCell(rowIndex, dataField);
            }, 1);
          }
        }
      }, 10);
    },
    async getConsumeProductBatch(code, batchNo, branchCode = "") {
      const branchFilter =
        branchCode === "" ? `` : `BranchCode eq '${branchCode}' and `;
      return ProductService.getCustomData({
        url: "api/ConsumeProductBatch",
        filter: `${branchFilter}ItemCode eq '${code}' and SourceTransactionType eq '' and SourceTransactionNo eq 0 and SourceTransactionLine eq 0 and SourceTransactionProcessCode eq '' and BatchNo eq '${batchNo}'`,
        select:
          "BatchNo,SupplierBatchNo,SupplierCode,SupplierName,ManufacturedDate,UseByDate,BestBeforeDate,AvailableQty,OnHand",
        top: 1,
      });
    },
    /**
     *
     * @param id
     */
    updateParent(id) {
      const currentData = this.dataGridLocalItemDataSource._array.find(
        (item) => item.ID === id,
      );
      if (currentData) {
        const totals = currentData.ChildDataSource._array.reduce(
          (calc, item) => {
            return {
              NewQuantity: calc.NewQuantity + item.NewQuantity,
              AdjustmentQuantity:
                calc.AdjustmentQuantity + item.AdjustmentQuantity,
              SupplierOutstandingQuantity:
                calc.SupplierOutstandingQuantity +
                item.SupplierOutstandingQuantity,
            };
          },
          {
            NewQuantity: 0,
            AdjustmentQuantity: 0,
            SupplierOutstandingQuantity: 0,
          },
        );

        // const parentItem = this.dataGridLocalItemDataSource.byKey(id);

        this.dataGridLocalItemDataSource.byKey(id).then((parentItem) => {
          if (parentItem) {
            let transactionUMConversion =
              currentData.TransactionUMConversion || 1;

            let newData = {};
            newData.TransactionUMConversion = transactionUMConversion;
            newData.NewQuantity = parentItem.OnHand + totals.AdjustmentQuantity;
            newData.AdjustmentQuantity = totals.AdjustmentQuantity;
            newData.AdjustedQty = totals.AdjustmentQuantity;
            this.setStockUMQuantity(newData, "NewQuantity");
            this.setStockUMQuantity(newData, "AdjustmentQuantity");
            this.setStockUMQuantity(newData, "AdjustedQty");
            newData.AdjustmentValue = (
              totals.AdjustmentQuantity * currentData.StandardCost
            ).round2Decimal();

            this.dataGridLocalItemDataSource.update(id, newData);
          }

          const dataGrid = this.getDataGridInstance();
          const index = dataGrid.getRowIndexByKey(id);
          dataGrid.repaintRows([index]);
        });

        // currentData.NewQuantity = totals.NewQuantity;
        // currentData.AdjustmentQuantity = totals.AdjustmentQuantity;
        // currentData.AdjustmentValue = (
        //   totals.AdjustmentQuantity * currentData.StandardCost
        // ).round2Decimal();

        // this.updateLocalDataSet("update", currentData);

        // setTimeout(() => {
        //   // dataGrid.saveEditData();
        //   dataGrid.repaint();
        // }, 50);
        // }
      }
    },
    async onBatchSupplierSet(newData, value) {
      newData.SupplierCode = value;

      // if(value !== null){
      //   const currentProduct = this.dataGridLocalItemDataSource._array.find(
      //       (item) => item.ID === currentRowData.ParentID
      //   );
      //
      //   if (currentProduct) {
      //     const SupplierList =
      //         await ProductSupplierService.getSupplierByProductCode(
      //             currentProduct.Code
      //         );
      //     if (SupplierList.data.value.length > 0) {
      //       const currentSupplier = SupplierList.data.value.find(
      //           (item) =>
      //               item.SupplierCode === value
      //       );
      //       if (!currentSupplier) {
      //         await this.showSupplierCodeAlert(
      //             currentRowData,
      //             currentProduct
      //         );
      //       }
      //     } else {
      //       await this.showSupplierCodeAlert(
      //           currentRowData,
      //           currentProduct
      //       );
      //     }
      //   }
      // }
      return newData;
    },
    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     * @returns {Promise<*>}
     */
    async onBatchSet(newData, value, currentRowData) {
      const currentProduct = this.dataGridLocalItemDataSource._array.find(
        (item) => item.ID === currentRowData.ParentID,
      );

      const transactionUMConversion = currentProduct
        ? currentProduct.TransactionUMConversion
        : 1;

      let branchCode = "";
      let productCode = "";
      if (currentProduct) {
        branchCode = currentProduct.BranchCode;
        productCode = currentProduct.Code;
      }
      const batchItems = await this.getConsumeProductBatch(
        productCode,
        value,
        branchCode,
      );

      newData.BatchNo = value;
      if (batchItems.data.value.length > 0) {
        let data = batchItems.data.value[0];
        if (data) {
          newData.ParentID = currentProduct.ID;
          newData.TransactionUMConversion = transactionUMConversion;
          newData.SupplierBatchNo = data.SupplierBatchNo;
          newData.SupplierCode = data.SupplierCode;

          newData.OnHandStockQuantity = data.OnHand.roundNumber(4);
          newData.AvailableStockQuantity = data.AvailableQty.roundNumber(4);
          newData.NewStockQuantity = data.AvailableQty.roundNumber(4);
          newData.AdjustmentStockQuantity = 0;

          this.setTransactionUMQuantity(newData, "OnHand");
          this.setTransactionUMQuantity(newData, "Available");
          this.setTransactionUMQuantity(newData, "NewQuantity");
          this.setTransactionUMQuantity(newData, "AdjustmentQuantity");

          if (data.ManufacturedDate)
            newData.ManufacturedDate = new Date(data.ManufacturedDate);
          if (data.BestBeforeDate)
            newData.BestBeforeDate = new Date(data.BestBeforeDate);
          if (data.UseByDate) newData.UseByDate = new Date(data.UseByDate);

          newData.FromBatchDB = true;
        } else {
          newData.FromBatchDB = false;
        }
      } else {
        newData.FromBatchDB = false;
      }
      if (newData.FromBatchDB) {
        let rowIndex = this.BatchGridInstance.getRowIndexByKey(
          currentRowData.BatchLineNo,
        );
        let cellElement = this.BatchGridInstance.getCellElement(
          rowIndex,
          "NewQuantity",
        );
        if (cellElement) {
          this.BatchGridInstance.focus(cellElement);
          setTimeout(() => {
            this.BatchGridInstance.editCell(rowIndex, "NewQuantity");
          }, 50);
        }
      }

      setTimeout(() => this.updateParent(currentRowData.ParentID), 200);
      const index = this.BatchGridInstance.getRowIndexByKey(
        currentRowData.BatchLineNo,
      );
      setTimeout(() => this.BatchGridInstance.repaintRows([index]), 0);
      if (this.isLastBatchRow(currentRowData)) {
        this.addNewBatchLine(currentProduct, false);
      }

      return newData;
    },

    /**
     * check and return if the current row is the last row of batch grid or not
     * @param currentRowData
     */
    isLastBatchRow(currentRowData) {
      let rowIndex = this.BatchGridInstance.getRowIndexByKey(
        currentRowData.BatchLineNo,
      );

      let totalRow = this.BatchGridInstance.getDataSource().totalCount();

      if (rowIndex === totalRow - 1) return true;
      else return false;
    },

    /**
     * on batch grid editable cells preparing
     * @param e
     */
    onBatchEditorPreparing(e) {
      if (
        e.dataField &&
        (e.dataField === "BatchNo" || e.dataField === "SupplierCode")
      ) {
        e.editorName = "dxSelectBox";
      }

      const dateDistEditList = ["UseByDate", "BestBeforeDate"];

      const disEditList = dateDistEditList.concat([
        "ManufacturedDate",
        "SupplierBatchNo",
        "SupplierCode",
      ]);

      if (e.row.data.FromBatchDB || e.row.data.IsBatchSup) {
        disEditList.map((disItem) => {
          if (e.row.data.IsBatchSup && disItem === "SupplierCode") return;
          const el = e.row.cells.find(
            (item) => item.column.dataField === disItem,
          );
          if (el) {
            el.column.allowEditing = false;
          }
        });
      }

      const gridInstance = e.component;
      const newValue = gridInstance.cellValue(
        e.row.rowIndex,
        "ManufacturedDate",
      );
      dateDistEditList.map((disItem) => {
        const el = e.row.cells.find(
          (item) => item.column.dataField === disItem,
        );

        if (el) {
          if (e.row.data.BatchNo && !e.row.data.FromBatchDB) {
            el.column.allowEditing = true;
          } else {
            el.column.allowEditing = !!newValue && !e.row.data.FromBatchDB;
          }
        }
      });
      if (e.editorOptions && e.editorOptions.onFocusOut) {
        let func = e.editorOptions.onFocusOut;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onFocusOut = (event) => {
            func(event, {
              data: e.row,
              component: e.component,
              index: e.index,
            });
          };
      }

      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            if (forwardTabbing(event) || onEnter(event)) {
              new Promise(() =>
                setTimeout(() => {
                  this.goToNextEditableComponent(e.row, e.index);
                }, 150),
              );
            } else if (backwardTabbing(event)) {
              event.event.preventDefault();
              this.goToPrevEditableComponent(e.row, e.index);
            }

            func(event, {
              data: e.row,
              component: e.component,
              index: e.index,
            });
          };
      }
    },
    /**
     *
     * @param row
     * @param index
     */
    goToNextEditableComponent(row, index) {
      const element = row.cells.find(
        (item) =>
          item.columnIndex > index &&
          item.column.allowEditing &&
          !item.column.readOnly,
      );
      if (element) {
        this.focusOnBatchEditableCell(row.rowIndex, element.column.name);
      } else {
        this.tabKey = true;
        setTimeout(() => this.goToBatchNextRow(row.data), 50);
      }
    },
    goToBatchNextRow(currentRowData) {
      const currentProduct = this.dataGridLocalItemDataSource._array.find(
        (product) => product.ID === currentRowData.ParentID,
      );
      if (currentProduct) {
        const batchIndex = currentProduct.ChildDataSource._array.findIndex(
          (batch) => batch.BatchLineNo === currentRowData.BatchLineNo,
        );
        if (batchIndex !== -1) {
          const nextBatch =
            currentProduct.ChildDataSource._array[batchIndex + 1];
          if (nextBatch) {
            const nextIndex = this.BatchGridInstance.getRowIndexByKey(
              nextBatch.BatchLineNo,
            );
            this.focusOnBatchEditableCell(nextIndex, "BatchNo");
          } else {
            const dataGrid = this.dataGrid;
            if (dataGrid) {
              let nextIndex = dataGrid.getRowIndexByKey(
                currentRowData.ParentID,
              );
              nextIndex = nextIndex + 2;
              this.focusOnEditableCell(nextIndex, "Code");
            }
          }
        }
      }
    },
    /**
     *
     * @param row
     * @param index
     */
    goToPrevEditableComponent(row, index) {
      const element = row.cells
        .reverse()
        .find(
          (item) =>
            item.columnIndex < index &&
            item.column.allowEditing &&
            !item.column.readOnly,
        );
      row.cells.reverse();
      if (element) {
        let instance = this.BatchGridInstance;
        instance.focus(element.cellElement);
        instance.editCell(row.rowIndex, element.columnIndex);
      } else {
        const currentProduct = this.dataGridLocalItemDataSource._array.find(
          (product) => product.ID === row.data.ParentID,
        );
        if (currentProduct) {
          const batchIndex = currentProduct.ChildDataSource._array.findIndex(
            (batch) => batch.BatchLineNo === row.data.BatchLineNo,
          );
          if (batchIndex !== -1) {
            const nextBatch =
              currentProduct.ChildDataSource._array[batchIndex - 1];
            if (nextBatch) {
              const nextIndex = this.BatchGridInstance.getRowIndexByKey(
                nextBatch.BatchLineNo,
              );
              this.focusOnBatchEditableCell(nextIndex, "AdjustmentQuantity");
            } else {
              const dataGrid = this.dataGrid;
              const parentRowIndex = dataGrid.getRowIndexByKey(
                row.data.ParentID,
              );
              this.focusOnEditableCell(parentRowIndex, "Comment");
            }
          }
        }
        // const dataGrid = this.dataGrid;
        // const nextIndex = dataGrid.getRowIndexByKey(row.data.ParentID);
        // this.focusOnEditableCell(nextIndex);
      }
    },
    /**
     *
     * @param e
     */
    onCustomItemCreating(e) {
      e.customItem = {
        BatchNo: e.text,
      };
    },
    /**
     *
     * @param e
     */
    onDateInKeyDown(e) {
      if (onEnter(e) || forwardTabbing(e)) {
        e.event.preventDefault();
        this.focusOnEditableCell(0, "Code");
      }
    },
    /**
     *
     * @param adjustmentQty
     * @param column
     * @param rowIndex
     */
    showAlertPositive(adjustmentQty, column, rowIndex = 0) {
      this.setValidationComeBack(column, rowIndex);
      this.displayValidationErrors([
        `You have entered an Adjustment Qty of ${adjustmentQty}, which will result in the New Qty being less than 0.0000. Please update the Adjustment Qty so that the New Qty is greater than or equal to 0.0000`,
      ]);
    },
    /**
     *
     * @param adjustmentQty
     * @param column
     * @param onHand
     * @param rowIndex
     */
    showAlertNegative(adjustmentQty, column, onHand, rowIndex = 0) {
      this.setValidationComeBack(column, rowIndex);
      this.displayValidationErrors([
        `You have entered an Adjustment Qty of ${adjustmentQty}, which will result in the New Qty being less than ${onHand}. Please update the Adjustment Qty so that the New Qty is greater than ${onHand}`,
      ]);
    },
    /**
     *
     * @param AdjustmentQuantity
     * @param value
     * @param currentRowData
     * @returns {boolean}
     */
    newQuantityValidation(AdjustmentQuantity, value, currentRowData) {
      const dataGrid = this.dataGrid;
      if (currentRowData.OnHand > 0) {
        if (value < 0) {
          this.showAlertPositive(
            AdjustmentQuantity,
            "NewQuantity",
            dataGrid.getRowIndexByKey(currentRowData.ID),
          );
          return false;
        }
      } else {
        if (value < currentRowData.OnHand) {
          this.showAlertNegative(
            AdjustmentQuantity,
            "NewQuantity",
            currentRowData.OnHand,
            dataGrid.getRowIndexByKey(currentRowData.ID),
          );
          return false;
        }
      }
      return true;
    },
    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     * @returns {boolean}
     */
    onNewQuantityChangeHandler(newData, value, currentRowData) {
      const adjustmentQuantity = value - currentRowData.OnHand;

      if (
        !this.newQuantityValidation(adjustmentQuantity, value, currentRowData)
      ) {
        return false;
      }

      newData.NewQuantity = value;
      newData.NewStockQuantity = (
        newData.NewQuantity * currentRowData.TransactionUMConversion
      ).roundNumber(4);

      newData.AdjustmentValue = currentRowData.AdjustmentValue || 0;
      if (currentRowData.IsBatchProduct === "No") {
        newData.AdjustmentQuantity = adjustmentQuantity.roundNumber(4);
        newData.AdjustmentStockQuantity = (
          newData.AdjustmentQuantity * currentRowData.TransactionUMConversion
        ).roundNumber(4);
        newData.AdjustmentValue = (
          newData.AdjustmentQuantity * currentRowData.StandardCost
        ).round2Decimal();
      }

      newData.ID = currentRowData.ID;

      this.updateCellValue(newData.ID, "AdjustmentQuantity", newData);
    },
    /**
     *
     * @param currentRowData
     * @param newData
     * @param newQuantity
     * @returns {boolean}
     */
    adjustmentQtyValidation(currentRowData, newData, newQuantity) {
      const dataGrid = this.dataGrid;
      if (currentRowData.OnHand > 0) {
        if (newQuantity < 0) {
          this.showAlertPositive(
            newData.AdjustmentQuantity,
            "AdjustmentQuantity",
            dataGrid.getRowIndexByKey(currentRowData.ID),
          );
          return false;
        }
      } else {
        if (newQuantity < currentRowData.OnHand) {
          this.showAlertNegative(
            newData.AdjustmentQuantity,
            "AdjustmentQuantity",
            currentRowData.OnHand,
            dataGrid.getRowIndexByKey(currentRowData.ID),
          );
          return false;
        }
      }
      return true;
    },
    setTransactionUMQuantity(newData, type) {
      if (type === "AdjustmentQuantity") {
        newData.AdjustmentQuantity = (
          newData.AdjustmentStockQuantity / newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "AdjustedQty") {
        newData.AdjustedQty = (
          newData.AdjustedStockQuantity / newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "NewQuantity") {
        newData.NewQuantity = (
          newData.NewStockQuantity / newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "OnHand") {
        newData.OnHand = (
          newData.OnHandStockQuantity / newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "Available") {
        newData.Available = (
          newData.AvailableStockQuantity / newData.TransactionUMConversion
        ).roundNumber(4);
      }
    },
    setStockUMQuantity(newData, type) {
      if (type === "AdjustmentQuantity") {
        newData.AdjustmentStockQuantity = (
          newData.AdjustmentQuantity * newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "AdjustedQty") {
        newData.AdjustedStockQuantity = (
          newData.AdjustedQty * newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "NewQuantity") {
        newData.NewStockQuantity = (
          newData.NewQuantity * newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "OnHand") {
        newData.OnHandStockQuantity = (
          newData.OnHand * newData.TransactionUMConversion
        ).roundNumber(4);
      } else if (type === "Available") {
        newData.AvailableStockQuantity = (
          newData.Available * newData.TransactionUMConversion
        ).roundNumber(4);
      }
    },

    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     * @returns {boolean}
     */
    onAdjustmentQtyChangeHandler(newData, value, currentRowData) {
      newData.isValid = true;
      validationErrorExists = false;
      if (currentRowData.ChildDataSource._array.length === 0) {
        const newQuantity = value + currentRowData.OnHand;
        if (
          !this.adjustmentQtyValidation(currentRowData, newData, newQuantity)
        ) {
          validationErrorExists = true;
          return false;
        }
        newData.TransactionUMConversion =
          currentRowData.TransactionUMConversion;
        newData.AdjustmentValue = currentRowData.AdjustmentValue || 0;
        newData.AdjustmentQuantity = value;
        newData.AdjustedQty = value;
        newData.NewQuantity = newQuantity;
        this.setStockUMQuantity(newData, "AdjustmentQuantity");
        this.setStockUMQuantity(newData, "AdjustedQty");
        this.setStockUMQuantity(newData, "NewQuantity");

        newData.AdjustmentValue = (
          newData.AdjustedQty * currentRowData.StandardCost
        ).round2Decimal();

        const gridDSData = this.dataGridLocalItemDataSource._array.find(
          (item) => item.ID === currentRowData.ID,
        );

        gridDSData.AdjustedQty = newData.AdjustedQty;
        gridDSData.AdjustmentQuantity = newData.AdjustmentQuantity;

        if (this.dataGrid) {
          let rowIndex = this.dataGrid.getRowIndexByKey(currentRowData.ID);
          if (rowIndex !== -1) {
            setTimeout(() => {
              this.$nextTick(() => {
                this.dataGrid.repaintRows([rowIndex]);
              });
            }, 1);
          }
        }
      }
      newData.ID = currentRowData.ID;
    },
    /**
     *
     * @param e
     */
    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (!e.data.isValid && this.baseView.mode !== "review") {
          e.rowElement.classList.add("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt")) {
            e.rowElement.classList.add("dx-row-alt-back");
            e.rowElement.classList.remove("dx-row-alt");
          }
        } else {
          e.rowElement.classList.remove("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt-back")) {
            e.rowElement.classList.remove("dx-row-alt-back");
            e.rowElement.classList.add("dx-row-alt");
          }
        }
      }
    },
    async getStockEnquiryOnHand(itemCode, branchCode) {
      if (!itemCode) return undefined;

      if (this.stockEnquiryDictionary[itemCode + branchCode] !== undefined) {
        return this.stockEnquiryDictionary[itemCode + branchCode];
      }

      let result = await ProductService.getStockEnquiryQuantity({
        filter: `BranchCode eq '${branchCode}' and ItemCode eq '${itemCode}'`,
      });

      this.stockEnquiryDictionary[itemCode + branchCode] = result.data.value
        .length
        ? result.data.value[0]
        : undefined;

      return this.stockEnquiryDictionary[itemCode + branchCode];
    },
    setTransactionUMData(newData, productItem) {
      newData.UMDataSource = productItem.StockTransferUnitOfMeasureList.filter(
        (x) => x.BranchCode === newData.BranchCode,
      );

      let defaultUMData = newData.UMDataSource.find(
        (x) => x.BranchCode === newData.BranchCode,
      );

      newData.SalesUM = defaultUMData
        ? defaultUMData.DefaultUnitOfMeasure
        : productItem.UnitOfMeasureCode;

      newData.TransactionUMConversion = defaultUMData
        ? defaultUMData.DefaultUnitOfMeasureConversion
        : 1;
    },
    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     */
    async onSetProduct(newData, value, currentRowData) {
      if (!value) return;

      const index = this.productDataSourceItems.findIndex(
        (x) => x.Code === value,
      );
      if (index === -1) {
        if (!this.productItemsLoaded) {
          this.showLoading(true);

          try {
            let res = await ProductService.getCustomData({
              url: "api/Product",
              count: false,
              top: 1,
              filter: `Inactive eq 'N' and Status eq 'A'`,
              expand:
                "StockTransferUnitOfMeasureList($select=UnitOfMeasure,BranchCode,ConversionFactor,DefaultUnitOfMeasure,DefaultUnitOfMeasureConversion,StandardCost)",
            });

            this.showLoading(false);
            if (res.data.value.length > 0) {
              const productItem = res.data.value[0];
              this.addItemToProductDataSource(productItem);
              newData.Code = productItem.Code;
              newData.ID = currentRowData.ID;
              newData.Description1 = productItem.Description1;
              newData.StockUM = productItem.UnitOfMeasureCode;
              newData.validCode = productItem.Code;
              newData.IsBatchProduct = productItem.IsBatchProduct;
              newData.BestBeforeShelfLife = productItem.BestBeforeShelfLife;
              newData.UseByShelfLife = productItem.UseByShelfLife;
              newData.IsManufacturedDateRequired =
                productItem.IsManufacturedDateRequired;
              newData.IsNewLine = false;

              if (!currentRowData.BranchCode)
                newData.BranchCode = this.defaultBranchCode;

              this.setTransactionUMData(newData, productItem);

              this.updateLocalDataSet("update", newData);
            } else {
              if (currentRowData.validCode)
                newData.Code = currentRowData.validCode;

              this.updateLocalDataSet("update", currentRowData);
              this.$store.dispatch("ui/setValidationSummaryVisible", true);
              this.setValidationComeBack("Product", currentRowData.ID);
              this.displayValidationErrors([
                "This Product does not exist. Please try again",
              ]);
            }
          } catch (error) {
            this.showLoading(false);
          }
        } else {
          if (currentRowData.validCode) newData.Code = currentRowData.validCode;

          this.updateLocalDataSet("update", currentRowData);
          this.$store.dispatch("ui/setValidationSummaryVisible", true);
          this.setValidationComeBack("Product", currentRowData.ID);
          this.displayValidationErrors([
            "This Product does not exist. Please try again",
          ]);
        }
      } else {
        newData.Code = value;

        const productItem = this.productDataSourceItems[index];
        newData.Description1 = productItem.Description1;
        newData.IsBatchProduct = productItem.IsBatchProduct;
        newData.BestBeforeShelfLife = productItem.BestBeforeShelfLife;
        newData.IsManufacturedDateRequired =
          productItem.IsManufacturedDateRequired;
        newData.UseByShelfLife = productItem.UseByShelfLife;
        newData.StockUM = productItem.UnitOfMeasureCode;
        newData.validCode = productItem.Code;
        newData.IsNewLine = false;
        newData.StandardCost = 0;
        newData.OnHand = 0;
        newData.OnHandStockQuantity = 0;
        newData.NewQuantity = 0;
        newData.NewStockQuantity = 0;
        newData.AdjustmentQuantity = 0;
        newData.AdjustmentStockQuantity = 0;
        newData.AdjustedQty = 0;

        if (!currentRowData.BranchCode)
          newData.BranchCode = this.defaultBranchCode;
        else newData.BranchCode = currentRowData.BranchCode;

        this.setTransactionUMData(newData, productItem);

        let stockEnquiry = await this.getStockEnquiryOnHand(
          productItem.Code,
          newData.BranchCode,
        );

        if (stockEnquiry) {
          // stockEnquiry.OnHand is in base unit(StockUM), so converting it to transaction UM
          newData.OnHand = (
            stockEnquiry.OnHand / newData.TransactionUMConversion
          ).roundNumber(4);
          newData.StandardCost =
            stockEnquiry.StandardCost * newData.TransactionUMConversion;
        } else {
          newData.OnHand = 0;
          newData.StandardCost = 0;
        }

        newData.AdjustmentQuantity = 0;
        newData.AdjustedQty = 0;

        if (newData.IsBatchProduct === "No") {
          newData.NewQuantity =
            newData.OnHand + currentRowData.AdjustmentQuantity;

          this.setStockUMQuantity(newData, "NewQuantity");
        }

        newData.productItem = productItem;
        newData.ID = currentRowData.ID;
        newData.AdjustmentQuantity = (
          (currentRowData.AdjustmentStockQuantity || 0) /
          newData.TransactionUMConversion
        ).roundNumber(4);
        newData.AdjustedQty = newData.AdjustmentQuantity;
        if (newData["IsBatchProduct"] !== "No") {
          newData["IsVisibleBatchData"] = true;
          newData.ChildDataSource = new ArrayStore({
            key: "BatchLineNo",
            data: [],
          });

          const batchItem = Object.assign({ ...batchItemBase });
          batchItem.ParentID = currentRowData.ID;
          batchItem.BestBeforeShelfLife = productItem.BestBeforeShelfLife;
          batchItem.IsManufacturedDateRequired =
            productItem.IsManufacturedDateRequired;
          batchItem.UseByShelfLife = productItem.UseByShelfLife;
          batchItem.SupplierCode = productItem.SupplierCode;
          batchItem.TransactionUMConversion = newData.TransactionUMConversion;

          newData.BatchLineNo = this.getMaxLineNo(newData);
          newData.ChildDataSource.insert(batchItem);
        } else {
          newData.ChildDataSource = new ArrayStore({
            key: "BatchLineNo",
            data: [],
          });
          setTimeout(
            () => this.getDataGridInstance().collapseRow(currentRowData.ID),
            10,
          );
        }
        this.updateLocalDataSet("update", newData);
        if (!currentRowData.Code && currentRowData.IsNewLine !== true)
          this.addNewRow(false);

        if (
          productItem.IsBatchProduct !== "No" ||
          productItem.IsBatchProduct !== currentRowData.IsBatchProduct
        ) {
          if (this.dataGrid) {
            let rowIndex = this.dataGrid.getRowIndexByKey(currentRowData.ID);
            if (rowIndex !== -1) {
              setTimeout(() => {
                this.$nextTick(() => {
                  this.dataGrid.repaintRows([rowIndex]);
                });
              }, 1);
            }
          }
        }
      }
    },
    /**
     *
     * @param newData
     * @param value
     * @param currentRowData
     */
    async onSetBranchCode(newData, value, currentRowData) {
      let itemWithAnyBatch =
        currentRowData.ChildDataSource._array &&
        currentRowData.ChildDataSource._array.some((child) => child.BatchNo);

      if (itemWithAnyBatch && currentRowData.IsBatchProduct != "No") {
        const index = this.dataGrid.getRowIndexByKey(currentRowData.ID);

        const confirmFromBranchChange = await this.$confirm(
          `All the selected batch information will be lost if the branch is changed. Do you want to continue?`,
          "Stock Adjustment",
        );

        if (!confirmFromBranchChange) {
          setTimeout(() => {
            if (index != -1) this.focusOnEditableCell(index, "Comment");
          }, 10);
          return;
        } else {
          currentRowData.ChildDataSource.clear();
          currentRowData.AdjustmentQuantity = 0;
          newData.AdjustmentQuantity = 0;
          newData.AdjustmentValue = 0;

          setTimeout(() => {
            const batchItem = { ...batchItemBase };
            batchItem.ParentID = currentRowData.ID;
            batchItem.BatchLineNo = this.getMaxLineNo(currentRowData);
            batchItem.BestBeforeShelfLife = currentRowData.BestBeforeShelfLife;
            batchItem.IsManufacturedDateRequired =
              currentRowData.IsManufacturedDateRequired;
            batchItem.SupplierCode = currentRowData.SupplierCode;
            batchItem.UseByShelfLife = currentRowData.UseByShelfLife;
            batchItem.TransactionUMConversion = currentRowData.TransactionUMConversion;
            currentRowData.ChildDataSource.insert(batchItem);

            if (index != -1) this.focusOnEditableCell(index, "Comment");
          }, 1);
        }
      }

      const productItem = currentRowData.productItem;
      newData.BranchCode = value;
      newData.OnHand = 0;
      newData.NewQuantity = 0;

      if (productItem) {
        let stockEnquiry = await this.getStockEnquiryOnHand(
          productItem.Code,
          newData.BranchCode,
        );

        newData.UMDataSource =
          productItem.StockTransferUnitOfMeasureList.filter(
            (x) => x.BranchCode === newData.BranchCode,
          );

        newData.StandardCost =
          newData.UMDataSource.find(
            (x) => x.UnitOfMeasure === currentRowData.SalesUM,
          ).StandardCost || 0;

        newData.OnHand = stockEnquiry.OnHand.roundNumber(4);

        newData.NewQuantity =
          newData.OnHand + currentRowData.AdjustmentQuantity;
      }

      if (newData.OnHand === 0 && currentRowData.AdjustedQty > 0)
        newData.NewQuantity =
          newData.OnHand + currentRowData.AdjustmentQuantity;
      const quantityValidation = this.newQuantityValidation(
        currentRowData.AdjustedQty,
        newData.NewQuantity,
        currentRowData,
      );
      if (quantityValidation) {
        this.adjustmentQtyValidation(
          newData,
          currentRowData.AdjustedQty,
          currentRowData,
        );
      }

      if (currentRowData.IsBatchProduct !== "No") {
        const index = this.dataGrid.getRowIndexByKey(currentRowData.ID);
        this.dataGrid.repaintRows([index]);
        this.detailKey++;
        this.dataGridLocalItemDataSource._array.forEach((product) => {
          if (product.IsBatchProduct !== "No") {
            setTimeout(
              () => this.getDataGridInstance().expandRow(product.ID),
              100,
            );
          }
        });
        // const batchInstance = this.getMasterDataInstance(currentRowData.ID);
        // batchInstance.option(
        //   "columns",
        //   this.getChildGridColumns({ data: { ...currentRowData, ...newData } })
        // );
        // batchInstance.repaint();
        setTimeout(
          () => this.getDataGridInstance().expandRow(currentRowData.ID),
          10,
        );
      }

      // if(currentRowData.IsBatchProduct !== "No"){
      //   setTimeout(() => this.getDataGridInstance().expandRow(currentRowData.ID), 10);
      // }else{
      //   setTimeout(() => this.getDataGridInstance().collapseRow(currentRowData.ID), 10);
      // }
    },
    /**
     *
     * @param itemCode
     */
    clearBatchGridSelection(itemCode) {
      if (this.$refs["dx-data-grid-master-comp"]) {
        this.dataGridLocalItemDataSource._array.forEach((element) => {
          if (itemCode && element.Code === itemCode) return;

          if (element.IsBatchProduct !== "No") {
            var batchGrid = this.getBatchGridInstance(
              element.Code + "-" + element.ID,
            );

            if (batchGrid) {
              batchGrid.clearSelection();
              batchGrid.option("focusedRowIndex", -1);
            }
          }
        });
      }
    },
    /**
     *
     * @param e
     */
    onEditingStart(e) {
      this.dataEditingCell = e;

      let data = e.data;

      if (
        e.column.dataField === "NewQuantity" ||
        e.column.dataField === "AdjustmentQuantity"
      ) {
        if (data.IsBatchProduct !== "No") {
          e.cancel = true;
        }
      }
    },
    /**
     *
     * @param e
     */
    onCellPrepared(e) {
      if (e.rowType === "data" && e.value < 0) {
        let cell = e.cellElement;
        cell.classList.add("negative-decimal");
        cell.setAttribute("id", "negative-decimal");
      }

      if (e.rowType === "data" && e.column.command === "expand") {
        let removeBatchExpand = false;
        if (
          e.data.IsBatchProduct === "Auto" &&
          e.data.IsVisibleBatchData !== undefined
        ) {
          if (e.data.IsVisibleBatchData === true) removeBatchExpand = false;
        }

        if (e.data.IsBatchProduct === "No" || removeBatchExpand) {
          if (e.cellElement && e.cellElement.classList) {
            e.cellElement.classList.add("gc-datagrid-expand");
            e.cellElement.classList.remove("dx-datagrid-expand");
            e.cellElement.childNodes[0].style.display = "none";
          }
        }
      }
    },
    /**
     *
     * @param e
     */
    onEditorPreparing(e) {
      if (
        e.dataField === "NewQuantity" ||
        e.dataField === "AdjustmentQuantity"
      ) {
        e.editorOptions.readOnly = e.row.data.IsBatchProduct !== "No";
        e.tabIndex = -1;
        e.editorOptions.tabIndex = -1;
      }

      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
              e: e,
            });
          };
      }

      if (e.editorOptions && e.editorOptions.onItemClick) {
        let func = e.editorOptions.onItemClick;
        if (this.baseView.mode !== "review" && e.parentType !== "searchPanel")
          e.editorOptions.onItemClick = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }

      if (e.dataField && e.dataField === "Code") {
        e.editorName = "dxSelectBox";
      }
    },
    /**
     *
     */
    addNewRow(isFocus = true) {
      let maxID = 0;

      maxID = Math.max(
        ...this.dataGridLocalItemDataSource._array.map((e) => e.ID),
      );

      if (this.dataGridLocalItemDataSource._array.length === 0) maxID = 0;
      let dataObj = Object.assign({ ...productItemBase });
      dataObj.ID = ++maxID;
      this.dataGridLocalItemDataSource.insert(dataObj);
      this.getDataGridInstance().collapseRow(dataObj.ID);
      if (isFocus) {
        setTimeout(() => {
          const rowIndex = this.getDataGridInstance().getRowIndexByKey(
            dataObj.ID,
          );
          this.focusOnEditableCell(rowIndex);
        }, 50);
      }
    },
    /**
     *
     * @param type
     * @param dataObj
     * @returns {Promise<void>}
     */
    async updateLocalDataSet(type, dataObj) {
      if (type === "insert") {
        if (
          this.dataGridLocalItemDataSource._array.length !== 0 &&
          this.dataGridLocalItemDataSource._array[
            this.dataGridLocalItemDataSource._array.length - 1
          ].Code
        ) {
          this.dataGridLocalItemDataSource.insert(dataObj);
        } else if (this.dataGridLocalItemDataSource._array.length === 0) {
          this.dataGridLocalItemDataSource.insert(dataObj);
        }
      } else if (type === "update") {
        this.dataGridLocalItemDataSource.update(dataObj.ID, dataObj);
      }
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    /**
     *
     * @param e
     * @param type
     * @returns {Promise<void>}
     */
    async addNewRowByPosition(e, type) {
      if (!e.row.data.Code) {
        this.setValidationComeBack("Product", e.row.data.ID);
        this.displayValidationErrors(["Product Code is required"]);
      } else {
        let dataObj = undefined;
        let maxID = Math.max(
          ...this.dataGridLocalItemDataSource._array.map((e) => e.ID),
        );

        if (type === "add-line") {
          dataObj = Object.assign({ IsNewLine: true }, { ...productItemBase });
        } else {
          dataObj = Object.assign({ ...e.row.data });
        }
        dataObj.ID = ++maxID;

        this.changes = [
          {
            type: "insert",
            data: dataObj,
            insertAfterKey: e.row.key,
          },
        ];

        const dataGrid = this.dataGrid;
        dataGrid.clearSelection();
        dataGrid.option("focusedRowIndex", -1);

        setTimeout(() => {
          dataGrid.saveEditData();
          dataGrid.refresh(true);
        }, 250);
      }
    },
    /**
     *
     * @param e
     */
    onSaved(e) {
      if (e.changes && e.changes.length > 0) {
        const change = e.changes[0];
        if (change.insertAfterKey !== undefined) {
          const element = change.data;
          let index = -1;

          index = this.dataGridLocalItemDataSource._array.findIndex(
            (x) => x.ID === change.insertAfterKey,
          );
          this.dataGridLocalItemDataSource._array.splice(
            this.dataGridLocalItemDataSource._array.length - 1,
            1,
          );
          this.dataGridLocalItemDataSource._array.splice(index + 1, 0, element);

          if (index !== -1)
            setTimeout(() => {
              this.focusOnEditableCell(index + 1);
            }, 100);
        }
      }
    },
    /**
     *
     * @param rowIndex
     * @param column
     */
    focusOnEditableCell(rowIndex, column = "Code") {
      const dataGrid = this.dataGrid;

      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);

      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },
    /**
     *
     * @param isValid
     * @param brokenRules
     * @returns {Promise<void>}
     */
    async saveTransaction(isValid, brokenRules) {
      const activeElement = document.activeElement;
      if (activeElement) {
        activeElement.blur();
      }

      setTimeout(async () => {
        if (!isValid) this.displayValidationSummary(brokenRules);
        else if (validationErrorExists) {
          return false;
        } else {
          await this.saveStockAdjustment();
        }
      }, 100);
    },
    /**
     *
     * @returns {{StockAdjustmentDetails: *[], DateIn: string}|boolean}
     */
    getValidatedData(isAlert = true) {
      let isEmpty = false;
      let payload = this.getSavePayload();

      const sumData = groupByAndSum(
        payload.StockAdjustmentDetails,
        ["BranchCode", "ItemCode"],
        "AdjustedQty",
      );

      const sumBatch = groupByAndSum(
        payload.singleRawData,
        ["BranchCode", "ItemCode", "BatchNo"],
        "AdjustmentQuantity",
      );

      const errorBatchData = [];

      const singleRawData = payload.singleRawData.reverse();

      sumBatch.map((sumBatchItem) => {
        const currentBatchItem = singleRawData.find(
          (origin) =>
            origin.Available > 0 &&
            origin.ItemCode === sumBatchItem.ItemCode &&
            origin.BatchNo === sumBatchItem.BatchNo &&
            origin.BranchCode === sumBatchItem.BranchCode &&
            sumBatchItem.AdjustmentQuantity + origin.Available < 0,
        );
        if (currentBatchItem) {
          errorBatchData.push(currentBatchItem);
        }
      });

      let latestErrorBatchData = null;
      let latestErrorData = null;
      if (errorBatchData.length > 0) {
        errorBatchData.forEach((errorBatchItem) => {
          const product = this.dataGridLocalItemDataSource._array.find(
            (source) => source.ID === errorBatchItem.ParentID,
          );

          if (product) {
            const errorBatches = product.ChildDataSource._array.filter(
              (batchSource) => batchSource.BatchNo === errorBatchItem.BatchNo,
            );

            if (errorBatches.length > 0) {
              const latestErrorBatchRow = errorBatches.length - 1;
              errorBatches[latestErrorBatchRow].isValid = false;
              latestErrorBatchData = errorBatches[latestErrorBatchRow];
            }
          }
        });
      }

      const errorData = [];
      sumData.map((sumDataItem) => {
        const currentItem = this.dataGridLocalItemDataSource._array.find(
          (origin) =>
            origin.OnHand > 0 &&
            origin.Code === sumDataItem.ItemCode &&
            origin.BranchCode === sumDataItem.BranchCode &&
            origin.AdjustedQty + origin.OnHand < 0,
        );
        if (currentItem) {
          errorData.push(sumDataItem);
        }
      });
      if (errorData.length > 0) {
        errorData.map((errorItem) => {
          const errorListData = this.dataGridLocalItemDataSource._array.filter(
            (sourceItem) => sourceItem.Code === errorItem.ItemCode,
          );
          if (errorListData.length > 0) {
            const latestErrorRow = errorListData.length - 1;
            errorListData[latestErrorRow].isValid = false;
            latestErrorData = errorListData[latestErrorRow];
          }
        });
      }
      this.dataGridLocalItemDataSource._array.map((item) => {
        if (item.IsBatchProduct !== "No") {
          item.ChildDataSource._array.map((batch) => {
            if (
              batch.BatchNo !== "" &&
              (batch.AdjustmentQuantity === 0 ||
                batch.SupplierCode === null ||
                typeof batch.SupplierCode === "undefined")
            ) {
              isEmpty = true;
              batch.isValid = false;
            }
          });
        }
      });

      // validate for batch controlled items where a single batch with multiple batch lines included
      this.dataGridLocalItemDataSource._array.map((item) => {
        if (item.IsBatchProduct !== "No" && item.AdjustedQty == 0) {
          var batches = item.ChildDataSource._array.filter(
            (batch) => batch.BatchNo !== "" && batch.AdjustmentQuantity !== 0,
          );

          if (batches.length > 1) {
            // check if the batch lines are all the same
            const batchNo = batches[0].BatchNo;
            const isSameBatch = batches.every(
              (batch) => batch.BatchNo === batchNo,
            );

            if (isSameBatch) {
              const currentBatchItem = singleRawData.find(
                (origin) =>
                  origin.ItemCode === item.Code &&
                  origin.BatchNo === batchNo &&
                  origin.BranchCode === item.BranchCode,
              );

              if (currentBatchItem) {
                errorBatchData.push(currentBatchItem);
              }

              batches.map((batch) => {
                batch.isValid = false;
              });
            }
          }
        }
      });

      // End validate

      if (errorBatchData.length > 0 || errorData.length > 0 || isEmpty) {
        this.dataGrid.repaint();
        if (latestErrorData !== null || latestErrorBatchData !== null) {
          if (latestErrorBatchData !== null) {
            this.BatchGridInstance = this.getMasterDataInstance(
              latestErrorBatchData.ParentID,
            );
            if (this.BatchGridInstance) {
              const index = this.BatchGridInstance.getRowIndexByKey(
                latestErrorBatchData.BatchLineNo,
              );
              if (index !== -1) {
                this.focusOnBatchEditableCell(index);
              }
            }
          } else {
            const index = this.dataGrid.getRowIndexByKey(latestErrorData.ID);
            this.setValidationComeBack("AdjustmentQuantity", index);
          }
        }
        if (isAlert) {
          this.displayValidationErrors([
            "One or more items on this Stock Adjustment have invalid data. Please update the highlighted records.",
          ]);
        }
        return false;
      } else {
        return payload;
      }
    },
    /**
     *
     * @returns {Promise<boolean>}
     */
    async saveStockAdjustment() {
      const payload = this.getValidatedData();
      if (!payload) {
        return false;
      }

      let manufacturedDateRequire = undefined;

      // const adjustedQty = this.getFooterItems.find((item) => item.dataField === "AdjustedQty")
      if (payload.StockAdjustmentDetails.length === 0) {
        this.displayValidationErrors([
          "Stock Adjustment has no detail. Please add one or more lines before attempting to save the transaction again.",
        ]);
        return;
      } else {
        for (let item of this.dataGridLocalItemDataSource._array) {
          if (!item.Code) break;
          else {
            for (let batch of item.ChildDataSource._array) {
              if (!batch.BatchNo) break;
              else {
                if (
                  batch.IsManufacturedDateRequired === "Y" &&
                  !batch.ManufacturedDate &&
                  !batch.FromBatchDB
                ) {
                  manufacturedDateRequire = batch;
                  break;
                }
              }
            }
          }
          if (manufacturedDateRequire) break;
        }

        if (manufacturedDateRequire) {
          await this.$alert(
            "Manufactured Date is Required",
            "Validation Error",
          );

          this.BatchGridInstance = this.getMasterDataInstance(
            manufacturedDateRequire.ParentID,
          );
          if (this.BatchGridInstance) {
            const index = this.BatchGridInstance.getRowIndexByKey(
              manufacturedDateRequire.BatchLineNo,
            );
            if (index !== -1) {
              this.focusOnBatchEditableCell(index, "ManufacturedDate");
            }
          }

          return;
        }
      }

      this.showLoading(true);
      payload.singleRawData = undefined;
      await StockAdjustmentService.saveStockAdjustment(payload)
        .then((res) => {
          this.showLoading(false);
          this.$notify(
            `Stock Adjustment ${res.data.Code} Successfully Saved`,
            "success",
          );
          this.setIgnoreRouteChangeConfirmation();
          this.loaded = false;
          this.$router.push({
            path: `/stock-adjustment`,
          });
        })
        .catch(async () => {
          // error.data.ValidationErrors.forEach((errorItem) => {
          //   if(errorItem.IsBatchProduct){
          //     const products = this.dataGridLocalItemDataSource._array.filter((item) => item === errorItem.ItemCode);
          //
          //     products.forEach((item) => {
          //       const batchItems = item.ChildDataSource._array.filter((batchItem) => batchItem.BatchNo === errorItem.BatchNo)
          //
          //       if(batchItems.length > 0){
          //         item.ChildDataSource._array[batchItems.length - 1].isValid = false
          //       }
          //     })
          //   }
          // })

          await this.refreshAllItems();
          this.showLoading(false);
          await this.$alert(
            "One or more items on this Stock Adjustment have invalid data. Please update the highlighted records.",
            "Server Error",
          );
        });
    },
    /**
     * Re-fetch all items to get all new data
     */
    async refreshAllItems() {
      const adjustments = this.dataGridLocalItemDataSource._array.filter(
        (item) => item.Code !== null,
      );
      await Promise.all(
        adjustments.map(async (item) => {
          const res = await ProductService.getByCodeExpand(item.Code);
          if (res.data.value.length > 0) {
            const productItem = res.data.value[0];
            item.Code = productItem.Code;
            item.Description1 = productItem.Description1;
            // item.SalesUM = productItem.UnitOfMeasureCode;
            item.validCode = productItem.Code;
            item.IsNewLine = false;

            const StockEnquiry = await this.getStockEnquiryOnHand(
              productItem.Code,
              item.BranchCode,
            );
            item.OnHand = StockEnquiry.OnHand.roundNumber(4);
            if (item.IsBatchProduct !== "No") {
              item.ChildDataSource._array.map(async (batchItem) => {
                const resBatchItem = await this.getConsumeProductBatch(
                  item.Code,
                  batchItem.BatchNo,
                  item.BranchCode,
                );
                if (resBatchItem.data.value.length > 0) {
                  const serverBatchItem = resBatchItem.data.value[0];
                  batchItem.OnHand = serverBatchItem.OnHand;
                  batchItem.Available = serverBatchItem.AvailableQty;
                  batchItem.NewQuantity =
                    serverBatchItem.AvailableQty + batchItem.AdjustmentQuantity;
                }
              });
            }
          }
          // this.updateLocalDataSet("update", item).then()
        }),
      );
      await this.dataGrid.refresh();
      setTimeout(() => this.getValidatedData(false), 1000);
    },
    /**
     *
     * @returns {{StockAdjustmentDetails: *[], DateIn: string}|null}
     */
    getSavePayload() {
      let manualBatchValidation = true;
      let payload = {
        DateIn: this.model.DateIn.toSaveDate(),
        StockAdjustmentDetails: [],
        singleRawData: [],
      };

      this.dataGridLocalItemDataSource._array.forEach((detail) => {
        if (!detail.Code) return;
        let detailObj = {
          AdjustedQty: detail.AdjustedStockQuantity,
          BranchCode: detail.BranchCode,
          Comment: detail.Comment,
          ItemCode: detail.Code,
          IsBatchProduct: detail.IsBatchProduct,
          StockAdjustmentProductBatches: [],
          StockUM: detail.StockUM,
          StockUMConversion: 1,
          TransactionUM: detail.SalesUM,
          TransactionUMConversion: detail.TransactionUMConversion,
          TransactionUMQty: detail.AdjustedQty,
        };

        if (detail.ChildDataSource) {
          detail.ChildDataSource._array.forEach((batch) => {
            if (!batch.BatchNo) return;
            let batchObj = {
              BatchNo: batch.BatchNo,
              SupplierCode: batch.SupplierCode,
              NewBatch: !batch.FromBatchDB ? "Y" : "N",

              AdjustedQty: batch.AdjustmentStockQuantity,
              OnHandQty: batch.OnHandStockQuantity,
              AvailableQty: batch.AvailableStockQuantity,
              TransactionUMQty: batch.AdjustmentQuantity,


              SupplierBatchNo: batch.SupplierBatchNo,
              ManufacturedDate: batch.ManufacturedDate
                ? new Date(batch.ManufacturedDate).toSaveDate()
                : batch.ManufacturedDate,
              BestBeforeDate: batch.BestBeforeDate
                ? new Date(batch.BestBeforeDate).toSaveDate()
                : batch.BestBeforeDate,
              UseByDate: batch.UseByDate
                ? new Date(batch.UseByDate).toSaveDate()
                : batch.UseByDate,
            };
            if (batch.FromBatchDB) {
              payload.singleRawData.push({
                ItemCode: detail.Code,
                BranchCode: detail.BranchCode,
                ParentID: batch.ParentID,
                BatchLineNo: batch.BatchLineNo,
                BatchNo: batch.BatchNo,
                OnHandQty: batch.OnHandStockQuantity,
                Available: batch.AvailableStockQuantity,
                AdjustmentQuantity: batch.AdjustedStockQuantity,
              });
            }
            detailObj.StockAdjustmentProductBatches.push(batchObj);
          });
        }

        // if (detail.IsBatchProduct !== "No") {
        //   this.reviewDataSource[index].ChildDataSource._array.forEach(
        //     (batch) => {
        //       if (!batch.BatchNo && batch.BatchAdjustmentQuantity !== 0)
        //         manualBatchValidation = false;
        //     }
        //   );
        // }

        payload.StockAdjustmentDetails.push(detailObj);
      });

      if (manualBatchValidation) return payload;
      else return null;
    },
    /**
     *
     * @param e
     */
    onProductCodeSelected(e) {
      if (e.selectedItem.Code !== null) {
        const index = this.productDataSourceItems.findIndex(
          (x) => x.Code === e.selectedItem.Code,
        );
        if (index === -1) {
          this.productDataSourceItems.push(e.selectedItem);
        } else {
          this.productDataSourceItems[index].StockEnquiryQuantities =
            e.selectedItem.StockEnquiryQuantities;
        }
      }
    },
    /**
     *
     * @param e
     * @param lineData
     */
    onEnterBranchCode(e, lineData) {
      if (onEnter(e) || forwardTabbing(e)) {
        setTimeout(() => {
          if (lineData && !lineData.data.Code) {
            this.setValidationComeBack("Code", lineData.rowIndex);
            this.displayValidationErrors(["Product is required"]);
          } else {
            if (lineData.data.IsBatchProduct !== "No") {
              this.focusOnEditableCell(lineData.rowIndex, "Comment");
              this.reFetchAllBatches(
                lineData.data.Code,
                lineData,
                lineData.data.BranchCode,
              );
            } else {
              this.focusOnEditableCell(lineData.rowIndex, "NewQuantity");
            }
          }
        }, 50);
      }
    },
    reFetchAllBatches(code, lineData, branchCode) {
      if (lineData.data.ChildDataSource._array.length > 0) {
        lineData.data.ChildDataSource._array.forEach((childItem) => {
          this.getConsumeProductBatch(code, childItem.BatchNo, branchCode).then(
            (result) => {
              if (result.data.value.length > 0) {
                const newBatch = result.data.value[0];
                childItem.OnHand = newBatch.OnHand;
                childItem.Available = newBatch.AvailableQty;
              } else {
                childItem.OnHand = 0;
                childItem.Available = 0;
              }
              childItem.NewQuantity =
                childItem.Available + childItem.AdjustmentQuantity;
            },
          );
        });
      }
    },
    /**
     *
     * @param e
     * @param lineData
     */
    onEnterProductCode(e, lineData) {
      if (onEnter(e) || forwardTabbing(e)) {
        let productCode = e.component.option("value");

        // there was no change in product code
        if (lineData.data.Code === productCode && !!productCode) {
          this.focusOnEditableCell(lineData.rowIndex, "SalesUM");
        } else {
          setTimeout(() => {
            if (lineData && !productCode) {
              this.setValidationComeBack("Code", lineData.rowIndex);
              this.displayValidationErrors(["Product is required"]);
              return;
            }

            if (lineData.data.IsBatchProduct !== "No") {
              this.getDataGridInstance().expandRow(lineData.data.ID);
            }

            this.focusOnEditableCell(lineData.rowIndex, "SalesUM");
          }, 800);
        }
      }
      if (backwardTabbing(e)) {
        if (e.event && typeof e.event.preventDefault === "function")
          e.event.preventDefault();
        if (lineData.rowIndex === 0) {
          const headerToolBarForm =
            this.$refs["gc-header-form-component"].$refs[
              "gc-form-header-toolbar"
            ].$refs["toolbar-form"].$refs["gc-form-comp"].instance;
          if (headerToolBarForm) {
            headerToolBarForm.getEditor("DateIn").instance().focus();
          }
        } else {
          const currentIndex =
            this.dataGridLocalItemDataSource._array.findIndex(
              (item) => item.ID === lineData.data.ID,
            );
          const prevData =
            this.dataGridLocalItemDataSource._array[currentIndex - 1];
          if (prevData.IsBatchProduct === "No") {
            const prevIndex = this.dataGrid.getRowIndexByKey(prevData.ID);
            this.focusOnEditableCell(prevIndex, "Comment");
          } else {
            const latestBatchRow =
              prevData.ChildDataSource._array[
                prevData.ChildDataSource._array.length - 1
              ];
            this.BatchGridInstance = this.getMasterDataInstance(prevData.ID);
            const batchIndex = this.BatchGridInstance.getRowIndexByKey(
              latestBatchRow.BatchLineNo,
            );
            // let column = "";
            this.focusOnBatchEditableCell(batchIndex, "ManufacturedDate");
            // if (latestBatchRow.FromBatchDB) {
            //
            // } else {
            //   /**
            //    * TODO - next to optimization on autodetect
            //    */
            //   if (
            //     prevData.UseByShelfLife > -1 &&
            //     prevData.BestBeforeShelfLife > -1
            //   ) {
            //     column = "UseByDate";
            //   } else if (prevData.UseByShelfLife > -1) {
            //     column = "UseByDate";
            //   } else if (prevData.BestBeforeShelfLife > -1) {
            //     column = "BestBeforeDate";
            //   }
            //   this.focusOnBatchEditableCell(batchIndex, column);
            // }
          }
          // this.focusOnEditableCell(lineData.rowIndex - 1, "Comment");
        }
      }
    },
    /**
     *
     * @param e
     * @param lineData
     */
    moveOnEnterKey(e, lineData) {
      if (onEnter(e) || forwardTabbing(e)) {
        if (e.event && typeof e.event.preventDefault === "function")
          e.event.preventDefault();
        if (lineData && !lineData.data.Code) {
          lineData.data.AdjustmentQuantity = 0;
          lineData.data.NewQuantity = 0;
          this.setValidationComeBack("Code", lineData.rowIndex);
          this.displayValidationErrors(["Product is required moveOnEnterKey"]);
        } else {
          if (lineData.column.dataField === "NewQuantity") {
            this.focusOnEditableCell(lineData.rowIndex, "AdjustmentQuantity");
          } else if (lineData.column.dataField === "AdjustmentQuantity") {
            this.focusOnEditableCell(lineData.rowIndex, "Comment");
          } else if (lineData.data.IsBatchProduct !== "No") {
            let batchGridInstance = this.getMasterDataInstance(
              lineData.data.ID,
            );
            if (batchGridInstance) {
              const cellElement = batchGridInstance.getCellElement(
                0,
                "BatchNo",
              );
              if (cellElement) {
                batchGridInstance.focus(cellElement);
                setTimeout(() => {
                  batchGridInstance.editCell(0, "BatchNo");
                }, 1);
              }
            }
          } else {
            const rowIndex = lineData.rowIndex + 1;
            this.focusOnEditableCell(rowIndex, "Code");
          }
        }
      } else if (
        backwardTabbing(e) &&
        lineData.column.dataField === "Comment" &&
        lineData.data.IsBatchProduct !== "No"
      ) {
        if (e.event && typeof e.event.preventDefault === "function")
          e.event.preventDefault();
        this.focusOnEditableCell(lineData.rowIndex, "BranchCode");
      }
    },
    /**
     *
     * @returns {Promise<void>}
     */
    async visibleProductSearch() {
      this.searchForItem = {
        visible: true,
        popUpCallBack: this.visibleProductSearchCallBack,
        productListQuery: {
          endpoint: "Product",
          expand:
            "StockTransferUnitOfMeasureList($select=UnitOfMeasure,BranchCode,ConversionFactor,DefaultUnitOfMeasure,DefaultUnitOfMeasureConversion,StandardCost)",
          filter: [["Status", "=", "A"], "and", ["Inactive", "=", "N"]],
          serverPaging: true,
          allowGrouping: false,
        },
        columns: [
          {
            screenField: "Comments",
            label: "Product",
            dataField: "Code",
          },
          {
            screenField: "Comments",
            label: "Description",
            dataField: "Description1",
          },
        ],
      };
    },
    /**
     *
     * @param productItem
     * @returns {Promise<void>}
     */
    async visibleProductSearchCallBack(productItem) {
      this.searchForItem = {
        visible: false,
        popUpCallBack: this.visibleProductSearchCallBack,
      };
      if (productItem) {
        const dataGrid = this.dataGrid;

        const idx = this.dataGridLocalItemDataSource._array.findIndex(
          (x) => !x.Code,
        );

        if (idx !== -1) {
          const data = this.dataGridLocalItemDataSource._array[idx];

          const obj = {
            Code: productItem.Code,
            ID: data.ID,
          };

          const index = this.productDataSourceItems.findIndex(
            (x) => x.Code === productItem.Code,
          );

          // add to product data source if not exists
          if (index === -1) {
            this.productDataSourceItems.push(productItem);
          }

          this.updateCellValue(data.ID, "Code", obj);

          this.dataGridLocalItemDataSource.update(data.ID, obj);
          dataGrid.refresh().then(() => {
            const rowIndex = dataGrid.getRowIndexByKey(data.ID);

            const cellElement = dataGrid.getCellElement(rowIndex, "BranchCode");
            dataGrid.focus(cellElement);
            dataGrid.editCell(rowIndex, "BranchCode");
          });
        }
      }
    },
    /**
     *
     * @param e
     */
    onRowRemoved(e) {
      if (this.dataGridLocalItemDataSource._array.length === 0) {
        this.addNewRow();
        e.component.refresh();
      } else {
        const lineData =
          this.dataGridLocalItemDataSource._array[
            this.dataGridLocalItemDataSource._array.length - 1
          ];
        if (lineData) {
          const batchKey = this.dataGrid.getRowIndexByKey(lineData.ID);
          if (batchKey !== -1) {
            this.focusOnEditableCell(batchKey);
          }
        }
      }
    },
    /**
     *
     * @param e
     */
    onBatchRowRemoved(e) {
      const lineParent = this.dataGridLocalItemDataSource._array.find(
        (item) => item.ID === e.data.ParentID,
      );
      this.updateParent(e.data.ParentID);
      if (lineParent.ChildDataSource._array.length === 0) {
        this.addNewBatchLine(lineParent);
      } else {
        const currentBatchData =
          lineParent.ChildDataSource._array[
            lineParent.ChildDataSource._array.length - 1
          ];
        if (currentBatchData) {
          const batchKey = this.BatchGridInstance.getRowIndexByKey(
            currentBatchData.BatchLineNo,
          );
          if (batchKey !== -1) {
            this.focusOnBatchEditableCell(batchKey);
          }
        }
      }
    },
    /**
     *
     * @param cssClass
     * @returns {DOMComponent<Properties>}
     */
    getBatchGridInstance(cssClass) {
      var layout = this.$refs["gc-base-layout"];
      var element = layout.$el.querySelector(`.${cssClass}`);

      return DataGrid.getInstance(element);
    },
    /**
     *
     * @param key
     * @param dataField
     * @param data
     */
    updateCellValue(key, dataField, data) {
      const dataGrid = this.dataGrid;

      const rowIndex = dataGrid.getRowIndexByKey(key);
      dataGrid.cellValue(rowIndex, dataField, data[dataField]);

      this.updateLocalDataSet("update", data);
    },
    /**
     *
     * @param item
     */
    addItemToProductDataSource(item) {
      this.productDataSourceItems.push(item);
    },
    /**
     *
     * @param column
     * @param key
     */
    setValidationComeBack(column, key) {
      this.validationColumn = column;
      this.validationDataRowKey = key;
    },
    /**
     *
     * @param e
     */
    onToolbarPreparing(e) {
      e.toolbarOptions.items.push({
        location: "before",
        widget: "dxButton",
        options: {
          icon: undefined,
          text: "Search",
          elementAttr: {
            class: "cx-color-button",
          },
          onClick: () => {
            this.visibleProductSearch();
          },
          visible: this.baseView.mode !== "review",
        },
      });

      e.toolbarOptions.items.push({
        location: "before",
        widget: "dxButton",
        options: {
          icon: this.expandChildGrid ? "chevronup" : "chevrondown",
          text: this.getLabel,
          onClick: () => {
            this.expandMasterDetailsSection();
          },
          onContentReady: (e) => {
            this.CollapseBtnContentReady = e;
          },
          visible: this.IsBatchAvailable,
        },
      });
    },
    /**
     *
     */
    expandMasterDetailsSection() {
      let gridInstance = this.getDataGridInstance();

      let items = this.dataGridLocalItemDataSource._array;
      if (this.expandChildGrid) {
        this.expandChildGrid = false;
        // gridInstance.collapseAll(-1);
        items.forEach((element, index) => {
          if (
            element.ChildDataSource &&
            element.ChildDataSource._array.length > 0
          ) {
            setTimeout(() => {
              gridInstance.collapseRow(element.LineNo);
            }, 100 * index);
          }
        });
      } else {
        this.expandChildGrid = true;
        items.forEach((element, index) => {
          if (
            element.ChildDataSource &&
            element.ChildDataSource._array.length > 0
          ) {
            setTimeout(() => {
              gridInstance.expandRow(element.LineNo);
            }, 100 * index);
          }
        });
      }
      setTimeout(() => {
        gridInstance.refresh(true).then(() => {
          if (this.CollapseBtnContentReady) {
            this.CollapseBtnContentReady.component.beginUpdate();
            this.CollapseBtnContentReady.component.option(
              "text",
              this.getLabel,
            );
            this.CollapseBtnContentReady.component.option(
              "icon",
              this.expandChildGrid ? "chevronup" : "chevrondown",
            );
            this.CollapseBtnContentReady.component.endUpdate();
          }
        });
      }, 100);
    },
    /**
     *
     */
    expandGrid() {
      this.$store.dispatch("ui/setGridExpand", !this.gridExpand);
      setTimeout(() => {
        const dataGrid = this.dataGrid;

        dataGrid.beginUpdate();
        dataGrid.updateDimensions();
        dataGrid.endUpdate();

        dataGrid.refresh(true);
      }, 500);
    },
    /**
     *
     * @returns {Promise<void>}
     */
    async setActiveAccountingPeriod() {
      let startDateResult =
        await StockAdjustmentService.getActiveAccountingPeriodStartDate();
      if (startDateResult) {
        let accountingPeriod = startDateResult.data.value[0];
        this.ui.DateAdjustedMin = new Date(accountingPeriod.StartDate);
      }

      let endDateResult =
        await StockAdjustmentService.getActiveAccountingPeriodEndDate();
      if (endDateResult) {
        let accountingPeriod = endDateResult.data.value[0];
        this.ui.DateAdjustedMax = new Date(accountingPeriod.EndDate);
      }
    },
    /**
     *
     * @returns {Promise<void>}
     */
    async initialized() {
      let isCopy = false;
      if (this.baseView.query) {
        if (this.baseView.query.copy === "true") {
          isCopy = true;
        }
      }

      await this.setBranchDataSourceItems();
      await this.$store.dispatch("screenFactors/getFactors", [65]);
      this.defaultBranchCode = getFactorByCode(65).Factorc;

      await this.setActiveAccountingPeriod();
      if (this.baseView.mode === "create" && !isCopy) {
        // await this.setProductDataSourceItems();

        // await this.$store.dispatch("screenFactors/getFactors", [65]);
        // this.defaultBranchCode = getFactorByCode(65).Factorc;
        let dataObj = Object.assign({ ...productItemBase });
        // dataObj.ChildDataSource = [Object.assign({ ...batchItemBase })];
        // dataObj.ChildDataSource.insert(batchItemBase);
        await this.updateLocalDataSet("insert", dataObj);
        this.loaded = true;
        this.showLoading(false);

        setTimeout(() => {
          this.focusOnInitialized();
        }, 100);
      } else {
        await StockAdjustmentReviewService.getByCode(this.baseView.id).then(
          (response) => {
            this.model.DateIn = response.data.value[0].DateIn;
            this.model.DateChanged = response.data.value[0].DateChanged;
            this.model.DateCreated = response.data.value[0].DateCreated;
            this.model.ChangedBy = response.data.value[0].ChangedBy;
            this.model.CreatedBy = response.data.value[0].CreatedBy;
            this.model.Status = response.data.value[0].FriendlyStatus;
            this.model.Code = this.baseView.id;
          },
        );

        if (isCopy) {
          this.model.DateIn = new Date();
          this.model.DateReceived = new Date();
          this.model.toFriendlyStatus = null;
          this.model.Status = "N";
        }

        await StockAdjustmentService.getCustomDataSocurce(
          "StockAdjustmentDetail",
          {
            filter: `StockAdjustmentCode eq ${this.baseView.id}`,
            expand:
              "Product($select=Code,BestBeforeShelfLife,IsManufacturedDateRequired,UseByShelfLife,StockEnquiryQuantities;$expand=StockEnquiryQuantities,StockTransferUnitOfMeasureList)",
          },
        )
          .then(async (res) => {
            const data = res.data.value;
            if (data && data.length > 0) {
              const IDs = [];
              for (const element of data) {
                const index = data.indexOf(element);
                element.IsVisibleBatchData = false;
                element.ChildDataSource = [];
                element.Description1 = element.ItemDescription;
                element.Code = element.ItemCode;
                element.SalesUM = element.TransactionUM;
                element.ID = index + 1;
                element.validCode = element.ItemCode;
                element.BestBeforeShelfLife =
                  element.Product.BestBeforeShelfLife;
                element.UseByShelfLife = element.Product.UseByShelfLife;
                element.IsManufacturedDateRequired =
                  element.Product.IsManufacturedDateRequired;
                element.isValid = true;
                element.AdjustmentValue =
                  element.AdjustedQty * element.StandardCost;
                element.StandardCost = (
                  element.StandardCost * element.TransactionUMConversion
                ).roundNumber(4);
                element.AdjustmentStockQuantity = element.AdjustedQty;
                element.AdjustedStockQuantity = element.AdjustedQty;
                element.AdjustmentQuantity = element.TransactionUMQty;
                element.AdjustedQty = element.TransactionUMQty;

                element.UMDataSource =
                  element.Product.StockTransferUnitOfMeasureList.filter(
                    (x) => x.BranchCode === element.BranchCode,
                  );

                element.productItem = element.Product;

                element.Product.StockEnquiryQuantities.forEach((stockItem) => {
                  if (stockItem.BranchCode === element.BranchCode) {
                    element.OnHandStockQuantity = stockItem.OnHand;
                    element.OnHand = (
                      stockItem.OnHand / element.TransactionUMConversion
                    ).roundNumber(4);
                  }
                });

                if (isCopy) {
                  element.NewQuantity = element.OnHand;
                  element.NewStockQuantity = element.OnHandStockQuantity;
                  element.AdjustedQty = 0;
                  element.AdjustedStockQuantity = 0;
                  element.AdjustmentQuantity = 0;
                  element.AdjustmentStockQuantity = 0;
                  element.AdjustmentValue = 0;
                  element.Comment = "";
                }

                this.dataGridLocalItemDataSource.push([
                  { type: "insert", data: element },
                ]);

                element["ChildDataSource"] = new ArrayStore({
                  key: "BatchLineNo",
                  data: [],
                });

                if (element["IsBatchProduct"] !== "No") {
                  if (isCopy) {
                    IDs.push(element.ID);
                    const batchItem = { ...batchItemBase };
                    this.IsBatchAvailable = true;
                    batchItem.ParentID = element.ID;
                    batchItem.BatchLineNo = this.getMaxLineNo(element) + 1;
                    element["IsVisibleBatchData"] = true;
                    element.ChildDataSource.insert(batchItem);
                  } else {
                    IDs.push(element.ID);
                    this.IsBatchAvailable = true;
                    element["IsVisibleBatchData"] = true;
                    element.AdjustedQty = 0;
                    element.AdjustedStockQuantity = 0;
                    await StockAdjustmentBatchService.getProductBatches(
                      element["StockAdjustmentCode"],
                      element["LineNo"],
                    ).then((batchData) => {
                      batchData.data.value.forEach((i, batchIndex) => {
                        i.TransactionUMConversion =
                          element.TransactionUMConversion;
                        i.ParentID = element.ID;
                        i.BatchLineNo = batchIndex;
                        i.SupplierDescription = i.SupplierName;
                        i.isValid = true;
                        if (i.UseByDate) i.UseByDate = new Date(i.UseByDate);

                        element.AdjustedStockQuantity =
                          element.AdjustedStockQuantity +
                          (isCopy ? 0 : Number(i.AdjustedQty));
                        i.AdjustmentStockQuantity = isCopy ? 0 : i.AdjustedQty;

                        element.AdjustedQty =
                          element.AdjustedQty +
                          (isCopy ? 0 : Number(i.TransactionUMQty));
                        i.AdjustmentQuantity = isCopy ? 0 : i.TransactionUMQty;

                        i.OnHandStockQuantity = i.OnHand;
                        i.OnHand =
                          i.OnHandStockQuantity /
                          element.TransactionUMConversion;
                        i.NewQuantity = i.OnHand;
                        i.NewStockQuantity = i.OnHand;
                        i.AvailableStockQuantity = i.AvailableQty;
                        i.Available =
                          i.AvailableStockQuantity /
                          element.TransactionUMConversion;
                      });
                      element["ChildDataSource"] = new ArrayStore({
                        key: "BatchLineNo",
                        data: batchData.data.value,
                      });
                    });
                  }
                }
                //
                this.updateLocalDataSet("update", element);
              }
              setTimeout(() => {
                let dataGrid = this.getDataGridInstance();
                if (dataGrid) {
                  dataGrid.refresh(true);
                  IDs.forEach((id) => dataGrid.expandRow(id));
                }
              }, 10);
            }
          })
          .catch((err) => {
            this.displayServerError(
              err,
              "Stock Adjustment Detail Request Failed",
            );
          });
        this.loaded = true;
        this.showLoading(false);
      }
    },
    /**
     *
     * @returns {Promise<void>}
     */
    // async setProductDataSourceItems() {
    //   this.productDataSourceItems = [];
    //   // let totalItemsCount = 0;
    //   this.setLoading = true;
    //
    //   await ProductService.getCustomData({
    //     url: "api/Product",
    //     count: true,
    //     top: 25,
    //     select: [
    //       "Code",
    //       "Description1",
    //     ],
    //     filter: `Inactive eq 'N' and Status eq 'A'`,
    //     expand:
    //       "StockEnquiryQuantities($select=BranchCode,OnHand,StandardCost,ItemCode)",
    //   }).then((res) => {
    //     // totalItemsCount = res.data["@odata.count"];
    //
    //     this.productDataSourceItems = this.productDataSourceItems.concat(
    //       res.data.value
    //     );
    //
    //     // this.reCallProductItemsList(totalItemsCount, 25, 25);
    //   });
    //   this.setLoading = false;
    // },
    /**
     *
     * @param totalItemsCount
     * @param top
     * @param skip
     * @returns {Promise<void>}
     */
    async reCallProductItemsList(totalItemsCount, top, skip) {
      if (skip < totalItemsCount)
        await ProductService.getCustomData({
          url: "api/Product",
          select: ["Code", "Description1"],
          count: true,
          top: top,
          skip: skip,
          filter: `Inactive eq 'N' and Status eq 'A'`,
          expand:
            "StockTransferUnitOfMeasureList($select=UnitOfMeasure,BranchCode,ConversionFactor,DefaultUnitOfMeasure,DefaultUnitOfMeasureConversion,StandardCost)",
        }).then((res) => {
          this.productDataSourceItems = this.productDataSourceItems.concat(
            res.data.value,
          );

          this.reCallProductItemsList(totalItemsCount, 25, skip + 25);
        });
      else this.productItemsLoaded = true;
    },
    /**
     *
     * @returns {Promise<void>}
     */
    async setBranchDataSourceItems() {
      let branchDS = BranchService.dataSourceConfig("api/Branch", {
        filter: [["Inactive", "=", "N"]],
        sort: [{ selector: "Code", asc: true }],
        preventPagination: true,
      });

      this.branchDataSourceItems = await branchDS.load();
    },
    focusOnInitialized() {
      const headerToolBarForm =
        this.$refs["gc-header-form-component"].$refs["gc-form-header-toolbar"]
          .$refs["toolbar-form"].$refs["gc-form-comp"].instance;
      if (headerToolBarForm) {
        headerToolBarForm.getEditor("DateIn").instance().focus();
      }
    },
    /**
     *
     * @param gridItems
     * @returns {Promise<void>}
     */
    async setFooterItems(gridItems) {
      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.model, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    /**
     *
     * @param dataField
     * @param items
     * @returns {number}
     */
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.map((x) => {
          if (x.CheckInclude || this.baseView.mode === "review")
            result += parseFloat(x[dataField]).roundNumber(4);
          else if (x.CheckInclude === undefined)
            result += parseFloat(x[dataField]).roundNumber(4);
        });

      return result;
    },
    onSalesUMFocusIn(e) {
      e.event.target.select();
    },
    async onSalesUMRowClick(e, cellInfo) {
      var rowIndex = cellInfo.rowIndex;
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.cellValue(rowIndex, "SalesUM", e.value);

      let selectedUMInfo = cellInfo.data.UMDataSource.find(
        (x) => x.UnitOfMeasure === e.value,
      );

      var stockEnquiry = await this.getStockEnquiryOnHand(
        cellInfo.data.Code,
        cellInfo.data.BranchCode,
      );

      var transactionUMConversion = selectedUMInfo
        ? selectedUMInfo.ConversionFactor
        : 1;

      cellInfo.data.TransactionUMConversion = transactionUMConversion;

      cellInfo.data.OnHandStockQuantity = stockEnquiry.OnHand;
      cellInfo.data.StandardCost = selectedUMInfo.StandardCost;

      this.setTransactionUMQuantity(cellInfo.data, "OnHand");
      this.setTransactionUMQuantity(cellInfo.data, "NewQuantity");
      this.setTransactionUMQuantity(cellInfo.data, "AdjustmentQuantity");
      this.setTransactionUMQuantity(cellInfo.data, "AdjustedQty");

      dataGrid.cellValue(
        rowIndex,
        "OnHand",
        cellInfo.data.OnHand.roundNumber(4),
      );

      dataGrid.cellValue(
        rowIndex,
        "StandardCost",
        cellInfo.data.StandardCost.roundNumber(4),
      );

      dataGrid.saveEditData();
      cellInfo.data.TransactionUMConversion = transactionUMConversion;

      // Update Child Quantities based on Transaction UM
      if (cellInfo.data.IsBatchProduct !== "No") {
        this.updateChildGridDataSource(cellInfo.data);
      }
    },

    updateChildGridDataSource(parentData) {
      if (parentData) {
        parentData.ChildDataSource._array.forEach((batchItem) => {
          batchItem.TransactionUMConversion =
            parentData.TransactionUMConversion;

          // skip when BatchNo is empty
          if (!batchItem.BatchNo || batchItem.BatchNo === "") return;

          this.setTransactionUMQuantity(batchItem, "OnHand");
          this.setTransactionUMQuantity(batchItem, "NewQuantity");
          this.setTransactionUMQuantity(batchItem, "Available");
          this.setTransactionUMQuantity(batchItem, "AdjustmentQuantity");
          this.setTransactionUMQuantity(batchItem, "AdjustedQty");
        });

        this.updateParent(parentData.ID);
      }
    },
  },
  watch: {
    /**
     *
     * @param val
     * @returns {Promise<void>}
     */
    async getValidationSummaryVisible(val) {
      if (!val && this.validationColumn === "Product") {
        const dataGrid = this.dataGrid;
        const rowIndex = dataGrid.getRowIndexByKey(this.validationDataRowKey);
        const cellElement = dataGrid.getCellElement(rowIndex, "Code");
        dataGrid.focus(cellElement);
        dataGrid.editCell(rowIndex, "Code");

        const index = this.dataGridLocalItemDataSource._array.findIndex(
          (x) => x.ID === this.validationDataRowKey,
        );
        if (index !== -1) {
          if (this.dataGridLocalItemDataSource._array[index].validCode) {
            dataGrid.cellValue(
              rowIndex,
              "Code",
              this.dataGridLocalItemDataSource._array[index].validCode,
            );
          }
        }

        this.validationColumn = undefined;
        this.validationDataRowKey = undefined;
      } else if (!val && this.validationColumn) {
        this.focusOnEditableCell(
          this.validationDataRowKey,
          this.validationColumn,
        );
        this.validationColumn = undefined;
        this.validationDataRowKey = undefined;
      }
    },
    /**
     *
     */
    dataGridLocalItemDataSource: {
      handler(val) {
        this.setFooterItems(val._array);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.sales-order-v2 {
  .dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row > td {
    vertical-align: middle !important;
  }
}

.alert-row {
  background-color: #ffcccc; /* Light red background */
  color: #a94442; /* Dark red text */
}

.stock-adjustment {
  .gc-header-form {
    .gc-form-header-toolbar {
      padding-bottom: unset !important;
    }
  }
  .edit-cell-datebox-field-border .dx-datebox::after {
    content: "";
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
  }

  .gc-require-validation::after,
  .gc-require-validation .dx-texteditor::after {
    content: "";
    border-bottom: 2px solid rgb(255 0 0) !important;
  }

  .css-v2
    .dx-datagrid.dx-gridbase-container
    .dx-datagrid-rowsview
    .dx-row.dx-data-row
    td {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }
}
</style>
